import { ruleBoxLines, ruleBrief } from "../approval-rule-brief.js";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import {
  Check,
  ChevronDown,
  Clock3,
  ExternalLink,
  FileUp,
  FilePenLine,
  LockKeyhole,
  LoaderCircle,
  MessageCircleMore,
  ShieldCheck,
  UserCheck,
  X
} from "lucide-react";
import {
  WORK_STATUS_LABELS,
  agentProposalMessageDraftMaxLength,
  describeWorkAttention,
  isAgentProposalMessageDraftWithinBound,
  unicodeScalarLength,
  type ApproveAgentProposalBundle,
  type AgentControlState,
  type AgentProposal,
  type AgentProposalEffect,
  type BoardSnapshot,
  type OpsPublicProposalProjection
} from "@business-ops/domain";
import { api, type WhatsAppConnection, type WhatsAppConversationRule } from "../api.js";
import {
  OpsAssistantPanel,
  waitForProposalOutcome as waitForExactProposalOutcome
} from "../components/ops-assistant.js";
import { SafeLinkedText } from "../components/safe-linked-text.js";
import { closeDetailsOnEscape, EmptyState } from "../components/ui.js";
import { fromLocalDateTime, memberLabel, toLocalDateTime } from "../helpers.js";

type Notify = (text: string, tone?: "success" | "error") => void;

export interface AgentWorkQueue {
  proposals: AgentProposal[];
  history?: AgentProposal[];
  // The Work surface must distinguish "proven empty" from "not loaded".
  // Optional keeps isolated render/tests and future channel adapters simple.
  loadState?: "LOADING" | "READY" | "STALE";
  refresh?: () => Promise<void>;
  conversations: WhatsAppConversationRule[];
  transportMode: AgentControlState["transportMode"];
  // Omitted only by isolated adapters/tests. Production supplies null while
  // checking and the exact connector state thereafter.
  whatsAppConnectionStatus?: WhatsAppConnection["status"] | null;
  openWhatsApp?: () => void;
  notify: Notify;
  removeProposal: (id: string) => void;
  replaceProposal: (proposal: AgentProposal) => void;
  replaceConversation: (conversation: WhatsAppConversationRule) => void;
  afterBoardChange: () => Promise<void>;
}

type ProposalActionProgress = {
  kind: "APPROVE" | "IDENTITY" | "REJECT" | "ROUTE" | "SNOOZE";
  label: string;
};

type ProposalDraftState = {
  effect: AgentProposalEffect;
  message: string;
  scheduleLocalDateTime: string;
  editing: boolean;
  rejecting: boolean;
  rejectReason: string;
  selectedRouteId: string;
  actionProgress: ProposalActionProgress | null;
  uploading: boolean;
};

type ProposalBundleState = {
  readyIds: ReadonlySet<string>;
  progress: string | null;
};

type DraftStatePatch<State> = Partial<State> | ((current: State) => Partial<State>);

type ProposalDraftStateContextValue = {
  scope: string;
  readProposal: (key: string, busyKey: string, initial: ProposalDraftState) => ProposalDraftState;
  updateProposal: (key: string, busyKey: string, initial: ProposalDraftState, patch: DraftStatePatch<ProposalDraftState>) => void;
  readBundle: (key: string, busyKey: string) => ProposalBundleState;
  updateBundle: (key: string, busyKey: string, patch: DraftStatePatch<ProposalBundleState>) => void;
};

const ProposalDraftStateContext = createContext<ProposalDraftStateContextValue | null>(null);
const ProposalDraftStateRevisionContext = createContext(0);

function initialProposalDraftState(proposal: AgentProposal): ProposalDraftState {
  return {
    effect: structuredClone(proposal.effect),
    message: proposal.messageDraft ?? "",
    scheduleLocalDateTime: proposal.effect.kind === "SEND_MESSAGE" ? proposal.effect.schedule?.localDateTime ?? "" : "",
    editing: false,
    rejecting: false,
    rejectReason: "NOT_NEEDED",
    selectedRouteId: "",
    actionProgress: null,
    uploading: false
  };
}

function proposalDraftStateKey(scope: string, proposal: AgentProposal): string {
  return `${scope}:${proposal.id}:${proposalDraftAuthorityKey(proposal)}`;
}

function proposalBundleStateKey(scope: string, unit: ApprovalUnit): string {
  return `${scope}:${unit.id}:${unit.proposals.map(proposalDraftAuthorityKey).join("|")}`;
}

/**
 * Keeps the ephemeral card state above the Chat/Approvals presentation split.
 * Standalone Review consumers intentionally omit this provider and retain the
 * ProposalCard-local fallback below.
 */
export function ProposalDraftStateProvider({ scope, children }: { scope: string; children: ReactNode }) {
  const [revision, rerender] = useState(0);
  const scopeRef = useRef(scope);
  const proposalStatesRef = useRef<Record<string, ProposalDraftState>>({});
  const bundleStatesRef = useRef<Record<string, ProposalBundleState>>({});
  const activeProposalKeyRef = useRef<Record<string, string>>({});
  const activeBundleKeyRef = useRef<Record<string, string>>({});
  // WHY [BOL-APPROVAL-BUSY-TRANSFER-001]: an edit/approval/upload may refresh
  // the server authority tuple before its async finally clears progress. Keep
  // only busy/action state on the stable scoped proposal/unit identity so a
  // remount or revision refresh cannot expose a second attempt. Falsifier: a
  // switched card becomes enabled while the original operation is pending.
  const proposalBusyRef = useRef<Record<string, Pick<ProposalDraftState, "actionProgress" | "uploading">>>({});
  const bundleBusyRef = useRef<Record<string, string | null>>({});
  if (scopeRef.current !== scope) {
    // A history/workspace scope change must not retain an old authority cache.
    // Busy overlays are scoped too; an old operation can settle harmlessly,
    // but must not repopulate a different authenticated/workspace scope.
    scopeRef.current = scope;
    proposalStatesRef.current = {};
    bundleStatesRef.current = {};
    activeProposalKeyRef.current = {};
    activeBundleKeyRef.current = {};
    proposalBusyRef.current = {};
    bundleBusyRef.current = {};
  }
  const readProposal = useCallback((key: string, busyKey: string, initial: ProposalDraftState) => {
    activeProposalKeyRef.current[busyKey] = key;
    const prefix = `${busyKey}:`;
    proposalStatesRef.current = Object.fromEntries(Object.entries(proposalStatesRef.current)
      .filter(([candidate]) => candidate === key || !candidate.startsWith(prefix)));
    const state = proposalStatesRef.current[key] ?? initial;
    const busy = proposalBusyRef.current[busyKey];
    return busy ? { ...state, ...busy } : state;
  }, []);
  const updateProposal = useCallback((key: string, busyKey: string, initial: ProposalDraftState, patch: DraftStatePatch<ProposalDraftState>) => {
    const activeKey = activeProposalKeyRef.current[busyKey];
    const current = activeKey === undefined || activeKey === key;
    const currentScope = scopeRef.current;
    const keyBelongsToScope = key.startsWith(`${currentScope}:`);
    const previous = proposalStatesRef.current[key] ?? initial;
    const previousBusy = proposalBusyRef.current[busyKey] ?? { actionProgress: null, uploading: false };
    const nextPatch = typeof patch === "function" ? patch(previous) : patch;
    const nextState = { ...previous, ...nextPatch };
    const draftChanged = Object.keys(nextPatch).some((field) => !Object.is(
      previous[field as keyof ProposalDraftState],
      nextState[field as keyof ProposalDraftState]
    ));
    const nextBusy = {
      actionProgress: "actionProgress" in nextPatch ? nextPatch.actionProgress ?? null : previousBusy.actionProgress,
      uploading: "uploading" in nextPatch ? nextPatch.uploading ?? false : previousBusy.uploading
    };
    const busyChanged = !Object.is(previousBusy.actionProgress, nextBusy.actionProgress)
      || !Object.is(previousBusy.uploading, nextBusy.uploading);
    if (!draftChanged && !busyChanged) return;
    if (draftChanged && current && keyBelongsToScope) {
      const prefix = `${busyKey}:`;
      proposalStatesRef.current = {
        ...Object.fromEntries(Object.entries(proposalStatesRef.current)
          .filter(([candidate]) => candidate === key || !candidate.startsWith(prefix))),
        [key]: nextState
      };
    }
    if (busyChanged && keyBelongsToScope) proposalBusyRef.current = { ...proposalBusyRef.current, [busyKey]: nextBusy };
    if ((draftChanged && current && keyBelongsToScope) || (busyChanged && keyBelongsToScope)) rerender((current) => current + 1);
  }, []);
  const readBundle = useCallback((key: string, busyKey: string) => {
    activeBundleKeyRef.current[busyKey] = key;
    const prefix = `${busyKey}:`;
    bundleStatesRef.current = Object.fromEntries(Object.entries(bundleStatesRef.current)
      .filter(([candidate]) => candidate === key || !candidate.startsWith(prefix)));
    const state = bundleStatesRef.current[key] ?? { readyIds: new Set<string>(), progress: null };
    return Object.prototype.hasOwnProperty.call(bundleBusyRef.current, busyKey)
      ? { ...state, progress: bundleBusyRef.current[busyKey] ?? null }
      : state;
  }, []);
  const updateBundle = useCallback((key: string, busyKey: string, patch: DraftStatePatch<ProposalBundleState>) => {
    const activeKey = activeBundleKeyRef.current[busyKey];
    const current = activeKey === undefined || activeKey === key;
    const currentScope = scopeRef.current;
    const keyBelongsToScope = key.startsWith(`${currentScope}:`);
    const previous = bundleStatesRef.current[key] ?? { readyIds: new Set<string>(), progress: null };
    const previousBusy = bundleBusyRef.current[busyKey] ?? previous.progress;
    const nextPatch = typeof patch === "function" ? patch(previous) : patch;
    const nextState = { ...previous, ...nextPatch };
    const nextReadyIds = nextPatch.readyIds;
    const readyIdsChanged = nextReadyIds !== undefined
      && (nextReadyIds.size !== previous.readyIds.size
        || [...nextReadyIds].some((id) => !previous.readyIds.has(id)));
    const progress = "progress" in nextPatch ? nextPatch.progress ?? null : previousBusy;
    const draftChanged = ("progress" in nextPatch && !Object.is(previous.progress, progress))
      || ("readyIds" in nextPatch && readyIdsChanged);
    const busyChanged = "progress" in nextPatch && !Object.is(previousBusy, progress);
    if (!draftChanged && !busyChanged) return;
    if (draftChanged && current && keyBelongsToScope) {
      const prefix = `${busyKey}:`;
      bundleStatesRef.current = {
        ...Object.fromEntries(Object.entries(bundleStatesRef.current)
          .filter(([candidate]) => candidate === key || !candidate.startsWith(prefix))),
        [key]: nextState
      };
    }
    if (busyChanged && keyBelongsToScope) bundleBusyRef.current = { ...bundleBusyRef.current, [busyKey]: progress };
    if ((draftChanged && current && keyBelongsToScope) || (busyChanged && keyBelongsToScope)) rerender((current) => current + 1);
  }, []);
  const value = useMemo<ProposalDraftStateContextValue>(() => ({
    scope,
    readProposal,
    updateProposal,
    readBundle,
    updateBundle
  }), [readBundle, readProposal, scope, updateBundle, updateProposal]);
  return <ProposalDraftStateContext.Provider value={value}>
    <ProposalDraftStateRevisionContext.Provider value={revision}>{children}</ProposalDraftStateRevisionContext.Provider>
  </ProposalDraftStateContext.Provider>;
}

function useProposalDraftState(proposal: AgentProposal): {
  state: ProposalDraftState;
  update: (patch: DraftStatePatch<ProposalDraftState>) => void;
} {
  const shared = useContext(ProposalDraftStateContext);
  void useContext(ProposalDraftStateRevisionContext);
  const [localState, setLocalState] = useState(() => initialProposalDraftState(proposal));
  const authorityKey = proposalDraftAuthorityKey(proposal);
  const initialState = useMemo(() => initialProposalDraftState(proposal), [authorityKey]);
  const key = shared ? proposalDraftStateKey(shared.scope, proposal) : authorityKey;
  const busyKey = shared ? `${shared.scope}:${proposal.id}` : proposal.id;
  useEffect(() => {
    if (!shared) setLocalState(initialState);
  }, [authorityKey, shared]);
  const update = useCallback((patch: DraftStatePatch<ProposalDraftState>) => {
    if (shared) {
      shared.updateProposal(key, busyKey, initialState, patch);
      return;
    }
    setLocalState((current) => {
      const nextPatch = typeof patch === "function" ? patch(current) : patch;
      return { ...current, ...nextPatch };
    });
  }, [busyKey, initialState, key, shared]);
  if (shared) return { state: shared.readProposal(key, busyKey, initialState), update };
  return {
    state: localState,
    update
  };
}

function useProposalBundleState(unit: ApprovalUnit): {
  state: ProposalBundleState;
  update: (patch: DraftStatePatch<ProposalBundleState>) => void;
} {
  const shared = useContext(ProposalDraftStateContext);
  void useContext(ProposalDraftStateRevisionContext);
  const [localState, setLocalState] = useState<ProposalBundleState>(() => ({ readyIds: new Set<string>(), progress: null }));
  const authorityKey = proposalBundleStateKey(shared?.scope ?? "local", unit);
  const busyKey = shared ? `${shared.scope}:${unit.id}` : unit.id;
  useEffect(() => {
    if (!shared) setLocalState({ readyIds: new Set<string>(), progress: null });
  }, [authorityKey, shared]);
  const update = useCallback((patch: DraftStatePatch<ProposalBundleState>) => {
    if (shared) {
      shared.updateBundle(authorityKey, busyKey, patch);
      return;
    }
    setLocalState((current) => {
      const nextPatch = typeof patch === "function" ? patch(current) : patch;
      return { ...current, ...nextPatch };
    });
  }, [authorityKey, busyKey, shared]);
  if (shared) return { state: shared.readBundle(authorityKey, busyKey), update };
  return {
    state: localState,
    update
  };
}

export type ApprovalUnit = {
  id: string;
  bundleId: string | null;
  expectedSize: number;
  complete: boolean;
  proposals: AgentProposal[];
};

export type PreparedProposalApproval = {
  proposal: AgentProposal;
  approval: ApproveAgentProposalBundle["approvals"][number];
};

export interface ProposalOutcomeActions {
  notify: Notify;
  afterMutation: () => Promise<void>;
}

export interface ProposalCardHandle {
  prepareApproval: () => Promise<PreparedProposalApproval>;
}

// WHY [BOL-APPROVAL-BUNDLE-WEB-001]: approvalBundle is the only server-owned
// identity allowed to combine effects in presentation. Grouping by Work ID,
// title, route, or wording could merge unrelated authority. An incomplete or
// position-conflicting bundle remains visible but cannot become a single or
// partial decision. Falsifier: one bundle renders twice, a missing member can
// be approved alone, or refresh order changes the exact approval order.
export function approvalUnits(proposals: readonly AgentProposal[]): ApprovalUnit[] {
  const unique = [...new Map(proposals.map((proposal) => [proposal.id, proposal])).values()];
  const grouped = new Map<string, AgentProposal[]>();
  const units: ApprovalUnit[] = [];
  for (const proposal of unique) {
    if (!proposal.approvalBundle) {
      units.push({
        id: `proposal:${proposal.id}`,
        bundleId: null,
        expectedSize: 1,
        complete: true,
        proposals: [proposal]
      });
      continue;
    }
    const current = grouped.get(proposal.approvalBundle.id) ?? [];
    current.push(proposal);
    grouped.set(proposal.approvalBundle.id, current);
  }
  for (const [bundleId, members] of grouped) {
    const ordered = [...members].sort((left, right) =>
      (left.approvalBundle?.position ?? Number.MAX_SAFE_INTEGER)
      - (right.approvalBundle?.position ?? Number.MAX_SAFE_INTEGER));
    const expectedSize = ordered[0]?.approvalBundle?.size ?? 0;
    const positions = new Set(ordered.map((proposal) => proposal.approvalBundle?.position));
    const complete = expectedSize >= 2
      && ordered.length === expectedSize
      && positions.size === expectedSize
      && ordered.every((proposal, index) => proposal.approvalBundle?.id === bundleId
        && proposal.approvalBundle.size === expectedSize
        && proposal.approvalBundle.position === index + 1);
    units.push({
      id: `bundle:${bundleId}`,
      bundleId,
      expectedSize,
      complete,
      proposals: ordered
    });
  }
  const firstIndex = new Map(unique.map((proposal, index) => [proposal.id, index]));
  return units.sort((left, right) =>
    Math.min(...left.proposals.map((proposal) => firstIndex.get(proposal.id) ?? Number.MAX_SAFE_INTEGER))
    - Math.min(...right.proposals.map((proposal) => firstIndex.get(proposal.id) ?? Number.MAX_SAFE_INTEGER)));
}

export function selectableFollowupRouteConversations(
  conversations: readonly WhatsAppConversationRule[]
): WhatsAppConversationRule[] {
  return conversations
    .filter((conversation) => conversation.kind === "GROUP"
      && conversation.policyVersion > 0
      && ["INTERNAL_REVIEW", "SCOPED_TEAM_REVIEW"].includes(conversation.rule))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function ReviewView({ board, notify, onCountChange, afterBoardChange }: {
  board: BoardSnapshot;
  notify: Notify;
  onCountChange: (count: number) => void;
  afterBoardChange: () => Promise<void>;
}) {
  const [proposals, setProposals] = useState<AgentProposal[] | null>(null);
  const [history, setHistory] = useState<AgentProposal[]>([]);
  const [conversations, setConversations] = useState<WhatsAppConversationRule[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [control, setControl] = useState<AgentControlState | null>(null);
  const [modeBusy, setModeBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [review, agentControl, whatsApp] = await Promise.all([
        api.review(),
        api.agentControl(),
        api.whatsAppConversations()
      ]);
      setProposals(review.proposals);
      setHistory(review.history);
      setConversations(whatsApp.conversations);
      setControl(agentControl);
      onCountChange(review.proposals.length);
    } catch (error) {
      setProposals([]);
      notify(error instanceof Error ? error.message : "Review could not be loaded.", "error");
    }
  }, [notify, onCountChange]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const events = new EventSource("/api/events");
    let timer: number | undefined;
    const refresh = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void load(), 150);
    };
    events.addEventListener("board-change", refresh);
    return () => { window.clearTimeout(timer); events.close(); };
  }, [load]);

  const changeMode = async (selection: "MANUAL" | "AUTO") => {
    if (!control || control.selection === selection) return;
    if (selection === "AUTO" && !window.confirm("Turn on only the action classes shown as eligible?")) return;
    setModeBusy(true);
    try {
      const updated = await api.updateAgentControl(selection, control.version);
      setControl(updated);
      notify(selection === "MANUAL" ? "Agent set to Manual review." : "Eligible actions set to Auto.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Agent mode did not change.", "error");
      void load();
    } finally {
      setModeBusy(false);
    }
  };

  const remove = (id: string) => {
    const nextLength = Math.max(0, (proposals?.length ?? 1) - 1);
    setActiveIndex((index) => Math.max(0, Math.min(index, nextLength - 1)));
    setProposals((current) => {
      const next = current?.filter((proposal) => proposal.id !== id) ?? [];
      onCountChange(next.length);
      return next;
    });
  };

  const activeProposal = proposals?.[Math.min(activeIndex, Math.max(0, proposals.length - 1))] ?? null;

  return (
    <div className="view-stack review-view">
      <section className="review-heading">
        <div><p className="eyebrow">Manual review</p><h1>Ready to approve</h1><p>See exactly what will change, why, and where—then approve it.</p></div>
      </section>

      <details className="ask-ops-disclosure">
        <summary>Ops Agent</summary>
        <OpsAssistantPanel notify={notify} afterBoardChange={afterBoardChange} />
      </details>

      <section className="agent-control-bar" aria-label="Agent controls">
        <div className="agent-control-bar__identity"><span><ShieldCheck size={19} /></span><div><strong>Agent mode</strong><small>{control?.selection === "AUTO" ? "Exact board updates only. Messages stay locked." : "Every agent action waits for approval."}</small></div></div>
        <div className="agent-mode-switch" role="group" aria-label="Manual or Auto">
          <button type="button" aria-pressed={control?.selection === "MANUAL"} className={control?.selection === "MANUAL" ? "is-active" : ""} disabled={!control || modeBusy} onClick={() => void changeMode("MANUAL")}>Manual</button>
          <button type="button" aria-pressed={control?.selection === "AUTO"} className={control?.selection === "AUTO" ? "is-active" : ""} disabled={!control?.autoAvailable || modeBusy} onClick={() => void changeMode("AUTO")} title={control?.autoAvailable ? "Use proven automatic actions" : "Auto unlocks after safety proof"}>Auto</button>
        </div>
        <span className="transport-lock"><LockKeyhole size={15} />{control?.transportMode === "BLOCKED" ? "WhatsApp sending locked" : control?.transportMode === "APPROVED_ONLY" ? "Approved sends only" : "Policy auto"}</span>
      </section>

      <section className="review-queue-heading"><p className="eyebrow">Ready to review</p><h2>Agent actions</h2></section>

      {proposals === null ? <p className="loading-line">Loading Review.</p> : proposals.length === 0 ? <section className="paper-panel"><EmptyState>Nothing needs approval right now.</EmptyState></section> : (
        <section className="review-list" aria-label={`${proposals.length} proposals ready for review`}>
          <nav className="review-queue-nav" aria-label="Follow-up queue">
            <button type="button" disabled={activeIndex === 0} onClick={() => setActiveIndex((index) => Math.max(0, index - 1))}>Previous</button>
            <strong>{activeIndex + 1} of {proposals.length}</strong>
            <button type="button" disabled={activeIndex >= proposals.length - 1} onClick={() => setActiveIndex((index) => Math.min(proposals.length - 1, index + 1))}>Next</button>
          </nav>
          {activeProposal ? (
            <ProposalCard
              key={activeProposal.id}
              proposal={activeProposal}
              board={board}
              queue={{
                proposals: proposals ?? [],
                conversations,
                transportMode: control?.transportMode ?? "BLOCKED",
                notify,
                removeProposal: remove,
                replaceProposal: (updated) => setProposals((current) => current?.map((item) => item.id === updated.id ? updated : item) ?? [updated]),
                replaceConversation: (updated) => setConversations((current) => current.map((item) => item.conversationId === updated.conversationId ? updated : item)),
                afterBoardChange
              }}
            />
          ) : null}
        </section>
      )}

      {history.length ? <RecentAgentActivity history={history} /> : null}
    </div>
  );
}

/**
 * WHY BOL-REVIEW-DRAFT-AUTHORITY-001: review refreshes replace proposal
 * objects and Chat/Approvals may remount the same card under different
 * surfaces. The scoped provider retains draft state under this exact server
 * authority tuple, and resets it only when that authority changes; otherwise
 * an unrelated sibling action or surface switch erases an unsaved edit.
 * Falsifier: a refresh with the same id/version/revision/state/message/effect
 * changes the rendered draft, or a changed server revision/state/effect leaves
 * the old local draft visible.
 */
export function proposalDraftAuthorityKey(proposal: Pick<AgentProposal, "id" | "messageDraft" | "currentRevision" | "state" | "version" | "effect">): string {
  return JSON.stringify([
    proposal.id,
    proposal.version,
    proposal.currentRevision,
    proposal.state,
    proposal.messageDraft,
    proposal.effect
  ]);
}

export const ProposalCard = forwardRef<ProposalCardHandle, {
  proposal: AgentProposal;
  board: BoardSnapshot;
  queue: AgentWorkQueue;
  embedded?: boolean;
  bundleMember?: boolean;
  bundleBusy?: boolean;
  onApprovalReadyChange?: (proposalId: string, ready: boolean) => void;
  settlePublicTurn?: () => Promise<void>;
}>(function ProposalCard({
  proposal,
  board,
  queue,
  embedded = false,
  bundleMember = false,
  bundleBusy = false,
  onApprovalReadyChange,
  settlePublicTurn
}, ref) {
  const { state: draft, update: updateDraft } = useProposalDraftState(proposal);
  const {
    effect,
    message,
    scheduleLocalDateTime,
    editing,
    rejecting,
    rejectReason,
    selectedRouteId,
    actionProgress,
    uploading
  } = draft;
  const setEffect = (value: AgentProposalEffect) => updateDraft({ effect: value });
  const setMessage = (value: string) => updateDraft({ message: value });
  const messageDraftLimit = agentProposalMessageDraftMaxLength(proposal.type);
  const setBoundedMessage = (value: string) => {
    if (isAgentProposalMessageDraftWithinBound(proposal.type, value)) updateDraft({ message: value });
  };
  const setScheduleLocalDateTime = (value: string) => updateDraft({ scheduleLocalDateTime: value });
  const setEditing = (value: boolean | ((current: boolean) => boolean)) => updateDraft({
    editing: typeof value === "function" ? value(editing) : value
  });
  const setRejecting = (value: boolean | ((current: boolean) => boolean)) => updateDraft({
    rejecting: typeof value === "function" ? value(rejecting) : value
  });
  const setRejectReason = (value: string) => updateDraft({ rejectReason: value });
  const setSelectedRouteId = (value: string) => updateDraft({ selectedRouteId: value });
  const setActionProgress = (value: ProposalActionProgress | null) => updateDraft({ actionProgress: value });
  const setUploading = (value: boolean) => updateDraft({ uploading: value });
  const busy = actionProgress !== null || bundleBusy;

  const isMessage = effect.kind === "SEND_MESSAGE";
  const isScheduled = isMessage && effect.schedule !== undefined;
  const unroutedFollowup = isMessage
    && proposal.conversationId === null
    && effect.conversationId === null
    && ["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE"].includes(proposal.type);
  const routeOptions = useMemo(
    () => selectableFollowupRouteConversations(queue.conversations),
    [queue.conversations]
  );
  const selectedRoute = routeOptions.find((conversation) => conversation.conversationId === selectedRouteId);
  const dirty = useMemo(
    () => JSON.stringify(effect) !== JSON.stringify(proposal.effect)
      || message !== (proposal.messageDraft ?? "")
      || (isScheduled && scheduleLocalDateTime !== effect.schedule?.localDateTime),
    [effect, isScheduled, message, proposal.effect, proposal.messageDraft, scheduleLocalDateTime]
  );
  const linkedWorkItem = proposal.workItemId
    ? board.workItems?.find((item) => item.id === proposal.workItemId)
    : undefined;
  const whyNow = isMessage
    && linkedWorkItem
    && ["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE"].includes(proposal.type)
      ? describeWorkAttention(linkedWorkItem, new Date(), board.workspace.timezone)
      : proposal.whyNow;
  const brief = effect.kind === "BUSINESS_RULE_UPDATE" ? ruleBrief(whyNow, effect.policy, proposal.title) : whyNow;
  const policyTransportBlocked = isMessage && queue.transportMode === "BLOCKED";
  const whatsAppConnectionBlocked = isMessage
    && !isScheduled
    && queue.whatsAppConnectionStatus !== undefined
    && queue.whatsAppConnectionStatus !== "CONNECTED";
  const messageTransportBlocked = policyTransportBlocked || whatsAppConnectionBlocked;
  const destinationPolicyMatches = !isMessage || proposal.conversationId === null || (
    proposal.destinationPolicyReady
    && proposal.destinationPolicyId !== null
    && proposal.destinationPolicyVersion !== null
    && proposal.destinationPolicyVersion === proposal.expectedVersions.conversationPolicyVersion
  );
  const targetRequired = isMessage && requiresNotifyTarget(proposal.type);
  const routedConversation = isMessage && proposal.conversationId
    ? queue.conversations.find((conversation) => conversation.conversationId === proposal.conversationId)
    : undefined;
  const targetMember = isMessage ? board.members.find((member) => member.id === effect.mentionMemberIds[0]) : undefined;
  const targetLabel = targetMember
    ? memberLabel(board.members, targetMember)
    : routedConversation?.kind === "DIRECT" ? routedConversation.label : "No responsible person";
  const targetRoute = isMessage ? proposal.targetRoute : undefined;
  const identityRecommendation = targetRoute?.recommendation ?? null;
  const targetReady = !targetRequired
    || (isMessage
      && effect.mentionMemberIds.length === 1
      && targetMember?.whatsAppLinked === true
      && targetRoute?.state === "READY");
  const targetStatus = !targetRequired
    ? routedConversation?.kind === "DIRECT" ? "Exact direct chat" : "No @mention required"
    : targetReady
    ? "Matched and in this group"
    : targetRoute?.state === "IDENTITY_REVIEW"
      ? "One match needs your confirmation"
      : targetRoute?.state === "IDENTITY_CONFLICT"
        ? "That WhatsApp identity is matched elsewhere"
      : targetRoute?.state === "NOT_IN_GROUP"
        ? "Matched, but not in this group"
      : targetRoute?.state === "MEMBERSHIP_UNKNOWN"
        ? "Checking current group members…"
      : targetRoute?.state === "ROUTE_MISSING" && targetMember?.whatsAppLinked === true
        ? "WhatsApp identity matched"
      : targetMember?.whatsAppIdentityState === "AMBIGUOUS" || targetRoute?.state === "IDENTITY_AMBIGUOUS"
      ? "WhatsApp found more than one possible person"
      : targetMember?.whatsAppIdentityState === "UNRESOLVED" || targetRoute?.state === "IDENTITY_UNRESOLVED"
        ? "No verified WhatsApp identity found"
        : "Checking current WhatsApp automatically…";
  const destination = proposal.destinationLabel ?? "Waiting for the agent to verify the WhatsApp route";
  const routedDestinationReady = isMessage
    && proposal.conversationId !== null
    && effect.conversationId === proposal.conversationId
    && proposal.destinationLabel !== null
    && destinationPolicyMatches;
  const destinationKind = routedConversation?.kind === "DIRECT" ? "PERSON" : "GROUP";
  const destinationHeading = `SENDS TO ${destinationKind}`;
  const destinationValue = destination.toLocaleUpperCase();
  const destinationLockReason = !routedDestinationReady
    ? "Route is still being verified. Send is locked."
    : !targetReady
      ? targetRoute?.state === "NOT_IN_GROUP" || identityRecommendation?.membership === "ABSENT"
        ? `${targetLabel} is not in this group`
        : targetStatus
      : whatsAppConnectionBlocked
        ? queue.whatsAppConnectionStatus === null
          ? "Checking the live WhatsApp connection. Send stays locked."
          : "WhatsApp needs relinking in More → WhatsApp. Send stays locked."
        : null;

  const decideIdentity = async (decision: "CONFIRM" | "REJECT") => {
    if (!identityRecommendation || !targetMember) return;
    setActionProgress({ kind: "IDENTITY", label: decision === "CONFIRM" ? "Confirming identity…" : "Rejecting identity…" });
    try {
      if (decision === "CONFIRM") {
        await api.confirmWhatsAppIdentityRecommendation(
          identityRecommendation.observedIdentityId,
          targetMember.id,
          identityRecommendation.sourceEventId
        );
        queue.notify(`${targetLabel} confirmed. Rechecking every affected draft.`);
      } else {
        await api.rejectWhatsAppIdentityRecommendation(
          identityRecommendation.observedIdentityId,
          targetMember.id,
          identityRecommendation.sourceEventId
        );
        queue.notify("Identity suggestion rejected.");
      }
      await queue.afterBoardChange();
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That identity decision did not save.", "error");
    } finally {
      setActionProgress(null);
    }
  };

  const setExactRoute = async () => {
    if (!unroutedFollowup || !selectedRoute) return;
    setActionProgress({ kind: "ROUTE", label: "Saving exact group…" });
    try {
      const updated = await api.routeProposal(proposal.id, {
        expectedVersion: proposal.version,
        conversationId: selectedRoute.conversationId,
        expectedPolicyVersion: selectedRoute.policyVersion
      });
      queue.replaceProposal(updated);
      queue.notify(`Exact group saved: ${selectedRoute.label}. Nothing sent.`);
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That exact group was not saved.", "error");
    } finally {
      setActionProgress(null);
    }
  };

  const save = async (): Promise<AgentProposal> => {
    if (!dirty) return proposal;
    // WHY [BOL-SCHEDULE-WEB-EDIT-001]: a future send is one frozen server-
    // resolved tuple. The browser may edit only the reviewed wall time and
    // message; the dedicated endpoint revokes any old authority and returns a
    // fresh revision. Falsifier: a generic edit keeps an approved old job live
    // or lets browser-derived UTC/timezone data reach execution.
    const updated = isScheduled
      ? await api.editScheduledProposal(proposal.id, {
          expectedVersion: proposal.version,
          revision: proposal.currentRevision,
          localDateTime: scheduleLocalDateTime,
          messageDraft: message
        })
      : await api.editProposal(proposal.id, {
          expectedVersion: proposal.version,
          effect,
          messageDraft: isMessage ? message : null
        });
    queue.replaceProposal(updated);
    setEditing(false);
    return updated;
  };

  const attachFile = async (file: File | undefined) => {
    if (!file || !isMessage) return;
    setUploading(true);
    try {
      const updated = await api.attachProposalFile(proposal.id, proposal.version, file);
      queue.replaceProposal(updated);
      queue.notify(`${file.name} attached. Review it, then ${isScheduled ? "schedule" : "send"}.`);
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That file did not attach.", "error");
    } finally {
      setUploading(false);
    }
  };

  const attachSourceFile = async (sourceEventId: string, displayName: string) => {
    if (!isMessage) return;
    setUploading(true);
    try {
      const updated = await api.attachProposalSourceFile(proposal.id, proposal.version, sourceEventId);
      queue.replaceProposal(updated);
      queue.notify(`${displayName} attached from its WhatsApp source. Review it, then ${isScheduled ? "schedule" : "send"}.`);
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That WhatsApp file did not attach.", "error");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async () => {
    if (effect.kind !== "SEND_MESSAGE" || !effect.attachment) return;
    setUploading(true);
    try {
      const { attachment: _removed, ...withoutAttachment } = effect;
      const updated = await api.editProposal(proposal.id, {
        expectedVersion: proposal.version,
        effect: withoutAttachment
      });
      queue.replaceProposal(updated);
      queue.notify(`File removed. Review the message, then ${isScheduled ? "schedule" : "send"}.`);
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That file did not remove.", "error");
    } finally {
      setUploading(false);
    }
  };

  const prepareApproval = async (): Promise<PreparedProposalApproval> => {
    let reviewed = proposal;
    if (reviewed.effect.kind === "SEND_MESSAGE") {
      if (targetRequired && (!targetReady || !targetMember)) throw new Error(
        targetMember?.whatsAppIdentityState === "AMBIGUOUS"
          ? "WhatsApp has more than one possible person. Send stays locked until the identity is clear."
          : targetMember?.whatsAppIdentityState === "UNRESOLVED"
            ? "The connected WhatsApp account has no verified identity for this person. Send stays locked."
            : "The live WhatsApp directory is still resolving this person."
      );
      if (reviewed.conversationId === null) {
        // WHY [BOL-WRONG-GROUP-UI-002]: Manual approval is approval of a
        // system-resolved route, not permission to invent one in the Send
        // click. The embedded Work flow therefore stays inert until a
        // verified provenance/policy route exists. Falsifier: an unrouted
        // task can expose an enabled Send or silently authorize a group.
        throw new Error("The agent has not verified the WhatsApp route yet. Send stays locked.");
      }
      if (isScheduled && dirty) {
        reviewed = await save();
      } else if (message !== (reviewed.messageDraft ?? "")) {
        reviewed = await api.editProposal(reviewed.id, {
          expectedVersion: reviewed.version,
          effect: reviewed.effect,
          messageDraft: message
        });
        queue.replaceProposal(reviewed);
      }
    } else {
      reviewed = await save();
    }
    const destinationConfirmation = reviewed.effect.kind === "SEND_MESSAGE"
      && reviewed.conversationId
      && reviewed.effect.conversationId === reviewed.conversationId
      && reviewed.destinationPolicyId && reviewed.destinationPolicyVersion
      ? {
          conversationId: reviewed.conversationId,
          policyId: reviewed.destinationPolicyId,
          policyVersion: reviewed.destinationPolicyVersion
        }
      : null;
    return {
      proposal: reviewed,
      approval: {
        proposalId: reviewed.id,
        expectedVersion: reviewed.version,
        revision: reviewed.currentRevision,
        destinationConfirmation
      }
    };
  };

  useImperativeHandle(ref, () => ({ prepareApproval }));

  const approve = async () => {
    setActionProgress({ kind: "APPROVE", label: proposalActionProgressLabel(effect) });
    try {
      const prepared = await prepareApproval();
      const reviewed = prepared.proposal;
      const approved = await api.approveProposal(
        prepared.approval.proposalId,
        prepared.approval.expectedVersion,
        prepared.approval.revision,
        prepared.approval.destinationConfirmation
      );
      // Approval has already committed exact version/revision authority. A
      // settlement refresh may fail independently and must never relabel that
      // durable decision as failed or cause another approval/effect attempt.
      try { await settlePublicTurn?.(); } catch {
        queue.notify("Approved, but the latest Luna result could not refresh yet.", "error");
      }
      if (approved.effect.kind === "SEND_MESSAGE" && approved.effect.schedule) {
        // WHY [BOL-SCHEDULE-WEB-APPROVAL-001]: approving a future send creates
        // durable permission for one later instant; it is not a transport
        // outcome. Remove the ready card only after Work/Calendar refresh, and
        // never poll or label the future job as sent. Falsifier: Schedule shows
        // Sent/Delivered before the Baileys receipt path runs at the due time.
        await queue.afterBoardChange();
        queue.removeProposal(proposal.id);
        queue.notify(`Scheduled for ${formatScheduledEffect(approved.effect)}. Not sent yet.`);
        if (settlePublicTurn) {
          void waitForExactProposalOutcome(reviewed.id)
            .then(() => settlePublicTurn())
            .catch(() => queue.notify("The scheduled result could not refresh yet.", "error"));
        }
        return;
      }
      // WHY [BOL-APPROVAL-STATE-001]: approval starts a separate final safety
      // check; it does not complete the overdue task. Refresh the durable task
      // projection before removing the editable draft so the drawer moves
      // directly to Final safety check instead of briefly claiming the agent is
      // merely watching. Falsifier: Send leaves only the overdue badge visible.
      const outcome = await waitForProposalOutcome(reviewed.id);
      try { await settlePublicTurn?.(); } catch {
        queue.notify("The final action is recorded, but the latest Luna result could not refresh yet.", "error");
      }
      if (outcome === "PENDING" && settlePublicTurn) {
        void waitForExactProposalOutcome(reviewed.id)
          .then(() => settlePublicTurn())
          .catch(() => queue.notify("The delayed final result could not refresh yet.", "error"));
      }
      await queue.afterBoardChange();
      queue.removeProposal(proposal.id);
      queue.notify(proposalOutcomeNotice(reviewed, outcome), proposalOutcomeTone(outcome));
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That proposal was not approved.", "error");
    } finally {
      setActionProgress(null);
    }
  };

  const reject = async () => {
    setActionProgress({ kind: "REJECT", label: "Rejecting…" });
    try {
      await api.rejectProposal(proposal.id, proposal.version, rejectReason);
      try { await settlePublicTurn?.(); } catch {
        queue.notify("Rejected, but the latest Luna result could not refresh yet.", "error");
      }
      queue.removeProposal(proposal.id);
      queue.notify("Proposal rejected.");
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That proposal was not rejected.", "error");
    } finally {
      setActionProgress(null);
    }
  };

  const snooze = async () => {
    const expiry = new Date(proposal.expiresAt).getTime();
    const until = Math.min(Date.now() + 60 * 60_000, expiry - 1_000);
    if (until <= Date.now()) { queue.notify("This proposal is too close to expiry to snooze.", "error"); return; }
    setActionProgress({ kind: "SNOOZE", label: "Snoozing…" });
    try {
      await api.snoozeProposal(proposal.id, proposal.version, new Date(until).toISOString());
      queue.removeProposal(proposal.id);
      queue.notify("Snoozed for up to one hour.");
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That proposal was not snoozed.", "error");
    } finally {
      setActionProgress(null);
    }
  };

  const blockedTargetLabel = targetRoute?.state === "IDENTITY_REVIEW"
    ? "Confirm identity above"
    : targetRoute?.state === "NOT_IN_GROUP"
      ? "Wrong group"
      : targetRoute?.state === "MEMBERSHIP_UNKNOWN"
        ? "Checking group"
        : targetMember?.whatsAppIdentityState === "AMBIGUOUS" || targetRoute?.state === "IDENTITY_AMBIGUOUS"
          ? "Needs clarification"
          : targetMember?.whatsAppIdentityState === "UNRESOLVED" || targetRoute?.state === "IDENTITY_UNRESOLVED"
            ? "Identity unresolved"
            : targetMember ? "Checking WhatsApp" : "Choose person";
  const approveLabel = !isMessage
    ? effect.kind === "NEW_WORK" ? "Add task" : effect.kind === "BUSINESS_RULE_UPDATE" ? "Save rule" : "Apply update"
    : whatsAppConnectionBlocked
      ? queue.whatsAppConnectionStatus === null ? "Checking WhatsApp" : "Reconnect WhatsApp"
    : policyTransportBlocked
      ? isScheduled ? "Scheduling locked" : "Sending locked"
      : !targetReady
        ? blockedTargetLabel
        : !routedDestinationReady
          ? "Waiting for route"
          : isScheduled ? "Schedule" : "Send";
  const primaryDisabled = busy || uploading || (whatsAppConnectionBlocked
    ? !queue.openWhatsApp
    : messageTransportBlocked
      || !targetReady
      || (isMessage && (message.trim().length === 0 || !routedDestinationReady || (isScheduled && !scheduleLocalDateTime))));
  useEffect(() => {
    if (!bundleMember || !onApprovalReadyChange) return;
    onApprovalReadyChange(proposal.id, !primaryDisabled);
    return () => onApprovalReadyChange(proposal.id, false);
  }, [bundleMember, onApprovalReadyChange, primaryDisabled, proposal.id]);
  const consequence = proposalConsequence(effect, destination, targetLabel, targetRequired);
  const effectLabel = proposalEffectLabel(effect);
  // WHY [BOL-APPROVAL-PROOF-001]: manual mode is useful only when the reviewer
  // can validate the agent without reopening the provider app. Every adapter
  // therefore renders the same source cards before Send: human labels, literal
  // stored excerpt/transcript, and time. The stable internal sourceEventId is
  // the exact locator when WhatsApp (or another channel) has no dependable
  // message permalink. Falsifier: Send appears before its proof or Review needs
  // a raw JID/thread ID/provider-specific component to explain the draft.
  const evidenceDisclosure = proposal.evidence.length > 0 ? (
    <details className="proposal-evidence">
      <summary>Proof · {proposal.evidence.length}<ChevronDown size={16} /></summary>
      <div>{proposal.evidence.map((evidence, index) => {
        const sourceLine = [
          evidence.channel ? channelLabel(evidence.channel) : null,
          evidence.conversationLabel,
          evidence.senderLabel,
          formatEvidenceTime(evidence.observedAt)
        ].filter(Boolean).join(" · ");
        return <blockquote key={`${evidence.sourceEventId ?? evidence.label}-${index}`}>
          <strong>{evidence.label}</strong>
          {sourceLine ? <small>{sourceLine}</small> : null}
          <p><SafeLinkedText text={evidence.excerpt} /></p>
          {isMessage && evidence.sourceEventId && evidence.attachment ? (
            <div className="proposal-source-attachment">
              <span><strong>{evidence.attachment.displayName}</strong><small>{evidence.attachment.kind.toLocaleLowerCase()} · {formatBytes(evidence.attachment.byteCount)}</small></span>
              <button className="secondary-button" type="button" disabled={busy || uploading} onClick={() => void attachSourceFile(evidence.sourceEventId!, evidence.attachment!.displayName)}><FileUp size={15} />{effect.attachment ? "Use this file instead" : "Attach this file"}</button>
            </div>
          ) : null}
          {evidence.sourceUrl && evidence.sourceUrlKind ? (
            <a className="proposal-source-link" href={evidence.sourceUrl} target="_blank" rel="noopener noreferrer">
              {evidence.sourceUrlKind === "EXACT_SOURCE" ? "Open original" : "Open conversation"}<ExternalLink size={14} />
            </a>
          ) : null}
        </blockquote>;
      })}</div>
    </details>
  ) : null;

  const CardElement = bundleMember ? "section" : "article";
  return (
    <CardElement
      className={`review-card review-card--${proposal.risk.toLowerCase()} ${embedded ? "review-card--embedded" : ""} ${bundleMember ? "review-card--bundle-member" : ""}`}
      aria-busy={busy || uploading}
      inert={bundleBusy || undefined}
    >
      {embedded ? <header className="review-card__compact-header">
        <span className={`proposal-effect-type proposal-effect-type--${effect.kind.toLocaleLowerCase().replaceAll("_", "-")}`}>{effectLabel}</span>
        <strong>{proposal.title}</strong>
        {proposal.risk === "HIGH" ? <small>High risk</small> : null}
      </header> : <header className="review-card__header">
          <div><span className={`proposal-type proposal-effect-type proposal-effect-type--${effect.kind.toLocaleLowerCase().replaceAll("_", "-")}`}>{effectLabel}</span><h2>{proposal.title}</h2></div>
          {proposal.risk !== "LOW" ? <span className="risk-badge">{proposal.risk.toLowerCase()} risk</span> : null}
        </header>}

      <section className="approval-brief" aria-label="Why this action is ready">{!embedded ? <span>Why now</span> : null}<p>{brief}</p></section>

      {!embedded ? <section className={`proposal-consequence ${isMessage ? "proposal-consequence--send" : "proposal-consequence--work"}`} aria-label="What approval will do">
        <span>Approving this will</span><p>{consequence}</p>
      </section> : null}

      {isMessage && identityRecommendation && targetMember ? (
        <section className="identity-recommendation" aria-label={`Confirm WhatsApp identity for ${targetLabel}`}>
          <header><span><UserCheck size={18} aria-hidden="true" /></span><div><small>One identity decision</small><strong>Is this {targetLabel}?</strong></div></header>
          <p>{identityRecommendation.evidence.conversationLabel} · {identityRecommendation.evidence.senderLabel}</p>
          <blockquote><SafeLinkedText text={identityRecommendation.evidence.excerpt} /></blockquote>
          {identityRecommendation.membership === "ABSENT" && routedDestinationReady ? <p className="identity-recommendation__warning">This identity is not in {destination}. Confirming the person will not unlock this draft; the agent still needs the correct group.</p> : null}
          <div><button className="secondary-button" type="button" disabled={busy} onClick={() => void decideIdentity("REJECT")}>Not this person</button><button className="primary-button" type="button" disabled={busy} onClick={() => void decideIdentity("CONFIRM")}><Check size={17} />Confirm {targetMember.displayName}</button></div>
        </section>
      ) : null}

      {isScheduled && effect.schedule ? (
        <section className="proposal-schedule" aria-label="Scheduled send time">
          <label><span>Send at</span><input type="datetime-local" step={1} value={scheduleLocalDateTime} disabled={busy} onChange={(event) => setScheduleLocalDateTime(event.target.value)} /></label>
          <div><span>Timezone</span><strong>{effect.schedule.timeZone} · UTC{effect.schedule.utcOffset}</strong></div>
          <p><Clock3 size={15} aria-hidden="true" />Not sent yet; will send at {formatReviewedSchedule(scheduleLocalDateTime, effect.schedule.timeZone, effect.schedule.utcOffset)} after approval.</p>
        </section>
      ) : null}

      {isMessage ? (
        <>
          <label className="message-draft-field"><span>Message</span><textarea rows={3} maxLength={messageDraftLimit * 2} value={message} disabled={busy} onChange={(event) => setBoundedMessage(event.target.value)} /><small>{unicodeScalarLength(message) ?? 0}/{messageDraftLimit}</small></label>
          <div className={`proposal-attachment ${effect.attachment ? "has-file" : "is-empty"}`} aria-label="WhatsApp attachment">
            <div><strong>{effect.attachment?.displayName ?? "No file attached"}</strong><small>{effect.attachment ? `${effect.attachment.kind.toLocaleLowerCase()} · ${formatBytes(effect.attachment.byteCount)}` : "Optional image, PDF, document, or MP4"}</small></div>
            <div className="proposal-attachment__actions">
              <label className="secondary-button"><FileUp size={16} />{uploading ? "Working…" : effect.attachment ? "Replace file" : "Attach file"}<input type="file" disabled={busy || uploading} onChange={(event) => { void attachFile(event.target.files?.[0]); event.currentTarget.value = ""; }} /></label>
              {effect.attachment ? <button className="quiet-review-action" type="button" disabled={busy || uploading} onClick={() => void removeFile()}><X size={15} />Remove</button> : null}
            </div>
          </div>
        </>
      ) : editing || bundleMember ? (
        <ProposalEditor type={proposal.type} board={board} effect={effect} setEffect={setEffect} message={message} setMessage={setMessage} completeTaskFields={bundleMember} />
      ) : (
        <ProposalPreview type={proposal.type} board={board} effect={effect} message={message} />
      )}

      {evidenceDisclosure}

      {isMessage ? (
        // WHY [BOL-DESTINATION-SIGNOFF-001]: an @name identifies the person mentioned
        // inside a group; it is not the transport destination. Showing both in
        // one recipient row made a real group send look like a DM. Put the
        // server-resolved transport target immediately beside Send, spell out
        // GROUP/PERSON in text, and keep the mention visually secondary. Red is
        // emphasis only, never the sole cue. Falsifier: a reviewer can read
        // a mention as the destination, or the exact group is not obvious before
        // approval at desktop and 360px.
        <section
          className={`approval-destination ${routedDestinationReady && targetReady && !whatsAppConnectionBlocked ? "is-ready" : "is-blocked"}`}
          aria-label={`${destinationHeading}: ${destinationValue}${targetRequired && destinationKind === "GROUP" ? `. Mentions: ${targetLabel}` : ""}`}
        >
          <MessageCircleMore size={19} aria-hidden="true" />
          <div>
            <span>{destinationHeading}</span>
            <strong>{destinationValue}</strong>
            {targetRequired && destinationKind === "GROUP" ? <small><b>MENTIONS:</b> {targetLabel}</small> : null}
            {isScheduled && effect.schedule ? <small className="approval-destination__time"><b>SEND TIME:</b> {formatReviewedSchedule(scheduleLocalDateTime, effect.schedule.timeZone, effect.schedule.utcOffset)}</small> : null}
            {destinationLockReason ? <p>{destinationLockReason}</p> : null}
          </div>
        </section>
      ) : null}

      {unroutedFollowup ? (
        // WHY [BOL-WORK-ROUTE-SELECTOR-001]: a missing Work route needs one
        // exact human choice from the live policy directory. This control only
        // records that route; it never approves or sends the draft. Falsifier:
        // the picker appears on a routed card, invents a group, or one click
        // creates approval/send authority.
        <section className="proposal-route-selector" aria-label="Set exact WhatsApp group">
          <label>
            <span>Exact WhatsApp group</span>
            <select
              aria-label="Choose exact group"
              value={selectedRouteId}
              disabled={busy || routeOptions.length === 0}
              onChange={(event) => setSelectedRouteId(event.target.value)}
            >
              <option value="">Choose exact group</option>
              {routeOptions.map((conversation) => (
                <option key={conversation.conversationId} value={conversation.conversationId}>
                  {conversation.label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="secondary-button"
            type="button"
            disabled={busy || !selectedRoute}
            onClick={() => void setExactRoute()}
          >
            {actionProgress?.kind === "ROUTE" ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <MessageCircleMore size={16} />}
            {actionProgress?.kind === "ROUTE" ? actionProgress.label : "Set group"}
          </button>
          {routeOptions.length === 0 ? <small>No eligible groups in the live directory.</small> : <small>This saves the route only. Nothing is sent.</small>}
        </section>
      ) : null}

      {rejecting ? <div className="reject-row"><select aria-label="Rejection reason" value={rejectReason} onChange={(event) => setRejectReason(event.target.value)}><option value="NOT_NEEDED">Not needed</option><option value="WRONG_FACTS">Wrong facts</option><option value="WRONG_RECIPIENT">Wrong recipient</option><option value="WRONG_TIMING">Wrong timing</option><option value="WRONG_TONE">Wrong tone</option><option value="DUPLICATE">Duplicate</option><option value="OTHER">Other</option></select><button type="button" disabled={busy} onClick={() => void reject()}>{actionProgress?.kind === "REJECT" ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <X size={16} />}{actionProgress?.kind === "REJECT" ? actionProgress.label : "Confirm reject"}</button><button type="button" disabled={busy} onClick={() => setRejecting(false)}>Cancel</button></div> : null}

      {/* WHY [BOL-ACTION-FEEDBACK-001]: an approval click starts guarded async
          work; a disabled button with unchanged copy looked frozen and invited
          another click. Name the exact in-flight effect immediately and expose
          it as a status message. Success remains the receipt-backed toast/card,
          never this animation. Falsifier: any approval appears idle while its
          request runs, or “Sent” appears before a terminal effect receipt. */}
      {actionProgress || uploading ? <div className="approval-action-progress" role="status" aria-live="polite" aria-atomic="true">
        <LoaderCircle className="spin" size={17} aria-hidden="true" />
        <span>{actionProgress?.label ?? "Preparing file…"}</span>
      </div> : null}

      {!bundleMember ? <footer className="review-card__actions" aria-busy={busy || uploading}>
        {/* WHY [BOL-WHATSAPP-RELINK-CTA-001]: a disabled button labelled
            Reconnect looked like another frozen approval. While live transport
            is unavailable, the same primary control is navigation to the QR
            surface and cannot call approve. Falsifier: it creates a receipt,
            stays inert, or opens anything other than this workspace's connector. */}
        <button className="primary-button" type="button" disabled={primaryDisabled} onClick={() => {
          if (whatsAppConnectionBlocked) {
            queue.openWhatsApp?.();
            return;
          }
          void approve();
        }}>{actionProgress?.kind === "APPROVE" ? <LoaderCircle className="spin" size={17} aria-hidden="true" /> : <Check size={17} />}{actionProgress?.kind === "APPROVE" ? actionProgress.label : approveLabel}</button>
        {!isMessage ? <button className="secondary-button" type="button" disabled={busy} onClick={() => setEditing((open) => !open)}><FilePenLine size={16} />{editing ? "Close edit" : "Edit"}</button> : null}
        {embedded ? <><button className="quiet-review-action" type="button" disabled={busy} onClick={() => setRejecting(true)}>Reject</button>{/* WHY [BOL-APPROVAL-MORE-001]: the closed mobile control must identify the hidden content as actions; bare “More” made the available Snooze action a mystery. Keep native details/summary activation and the existing 44px layout. Falsifier: the closed 360px card does not say “More actions”, or opening it fails to reveal “Snooze 1h”. */}<details className="proposal-more-actions" onKeyDown={closeDetailsOnEscape}><summary>More actions</summary><div><button className="quiet-review-action" type="button" disabled={busy} onClick={() => void snooze()}>{actionProgress?.kind === "SNOOZE" ? <LoaderCircle className="spin" size={15} aria-hidden="true" /> : <Clock3 size={15} />}{actionProgress?.kind === "SNOOZE" ? actionProgress.label : "Snooze 1h"}</button></div></details></> : <><button className="quiet-review-action" type="button" disabled={busy} onClick={() => setRejecting(true)}>Reject</button><button className="quiet-review-action" type="button" disabled={busy} onClick={() => void snooze()}>{actionProgress?.kind === "SNOOZE" ? <LoaderCircle className="spin" size={15} aria-hidden="true" /> : <Clock3 size={15} />}{actionProgress?.kind === "SNOOZE" ? actionProgress.label : "Snooze 1h"}</button></>}
      </footer> : null}
    </CardElement>
  );
});

export type ApproveProposalBundleClient<T = unknown> = (
  bundleId: string,
  approvals: ApproveAgentProposalBundle["approvals"]
) => Promise<T>;

export async function runAtomicApprovalBundle<T>(unit: ApprovalUnit, dependencies: {
  prepareProposal: (proposalId: string) => Promise<PreparedProposalApproval>;
  approveBundle: ApproveProposalBundleClient<T>;
  afterAtomicSuccess?: (prepared: readonly PreparedProposalApproval[], result: T) => Promise<void> | void;
}): Promise<{ prepared: PreparedProposalApproval[]; result: T }> {
  if (!unit.bundleId || !unit.complete) {
    throw new Error("Every linked action must be present before this approval can continue.");
  }
  const prepared = await Promise.all(unit.proposals.map((proposal) => dependencies.prepareProposal(proposal.id)));
  for (const [index, item] of prepared.entries()) {
    const expected = unit.proposals[index];
    const bundle = item.proposal.approvalBundle;
    if (!expected || item.approval.proposalId !== expected.id || bundle?.id !== unit.bundleId
      || bundle.position !== index + 1 || bundle.size !== unit.expectedSize) {
      throw new Error("A linked action changed while it was being reviewed. Nothing was approved.");
    }
  }
  const approvals = prepared.map((item) => item.approval);
  const result = await dependencies.approveBundle(unit.bundleId, approvals);
  await dependencies.afterAtomicSuccess?.(prepared, result);
  return { prepared, result };
}

export function ApprovalUnitCard({ unit, board, queue, embedded = false, approveBundle, settlePublicTurn }: {
  unit: ApprovalUnit;
  board: BoardSnapshot;
  queue: AgentWorkQueue;
  embedded?: boolean;
  approveBundle: ApproveProposalBundleClient;
  settlePublicTurn?: () => Promise<void>;
}) {
  if (!unit.bundleId) {
    const proposal = unit.proposals[0];
    return proposal ? <ProposalCard proposal={proposal} board={board} queue={queue} embedded={embedded} {...(settlePublicTurn ? { settlePublicTurn } : {})} /> : null;
  }
  return <ProposalBundleCard unit={unit} board={board} queue={queue} embedded={embedded} approveBundle={approveBundle} {...(settlePublicTurn ? { settlePublicTurn } : {})} />;
}

export function ProposalBundleCard({ unit, board, queue, embedded = false, approveBundle, settlePublicTurn }: {
  unit: ApprovalUnit;
  board: BoardSnapshot;
  queue: AgentWorkQueue;
  embedded?: boolean;
  approveBundle: ApproveProposalBundleClient;
  settlePublicTurn?: () => Promise<void>;
}) {
  const memberRefs = useRef(new Map<string, ProposalCardHandle>());
  const { state: bundleState, update: updateBundle } = useProposalBundleState(unit);
  const { readyIds, progress } = bundleState;
  const setProgress = (value: string | null) => updateBundle({ progress: value });
  const setMemberRef = useCallback((proposalId: string, handle: ProposalCardHandle | null) => {
    if (handle) memberRefs.current.set(proposalId, handle);
    else memberRefs.current.delete(proposalId);
  }, []);
  const setMemberReady = useCallback((proposalId: string, ready: boolean) => {
    updateBundle((current) => {
      const next = new Set(current.readyIds);
      if (ready) next.add(proposalId);
      else next.delete(proposalId);
      return { readyIds: next };
    });
  }, [updateBundle]);
  const allReady = unit.complete && unit.proposals.every((proposal) => readyIds.has(proposal.id));
  const approvalLabel = approvalBundleActionLabel(unit.proposals);

  const approve = async () => {
    setProgress(APPROVAL_BUNDLE_PROGRESS.preparing);
    try {
      const { prepared } = await runAtomicApprovalBundle(unit, {
        prepareProposal: async (proposalId) => {
          const handle = memberRefs.current.get(proposalId);
          if (!handle) throw new Error("One linked action is still loading. Nothing was approved.");
          return handle.prepareApproval();
        },
        approveBundle: async (bundleId, approvals) => {
          setProgress(APPROVAL_BUNDLE_PROGRESS.applying);
          return approveBundle(bundleId, approvals);
        },
        afterAtomicSuccess: (reviewed) => {
          setProgress(APPROVAL_BUNDLE_PROGRESS.done);
          for (const item of reviewed) queue.removeProposal(item.proposal.id);
        }
      });
      queue.notify(approvalBundleSuccessNotice(prepared.map((item) => item.proposal)));
      try { await settlePublicTurn?.(); } catch {
        queue.notify("Approved together, but the latest Luna result could not refresh yet.", "error");
      }
      const immediate = prepared.filter((item) => !(
        item.proposal.effect.kind === "SEND_MESSAGE" && item.proposal.effect.schedule
      ));
      if (immediate.length > 0 && settlePublicTurn) {
        void Promise.all(immediate.map((item) => waitForExactProposalOutcome(item.proposal.id)))
          .then(() => settlePublicTurn())
          .catch(() => queue.notify("The final linked result could not refresh yet.", "error"));
      }
      const scheduled = prepared.filter((item) => (
        item.proposal.effect.kind === "SEND_MESSAGE" && item.proposal.effect.schedule
      ));
      if (scheduled.length > 0 && settlePublicTurn) {
        void Promise.all(scheduled.map((item) => waitForExactProposalOutcome(item.proposal.id)))
          .then(() => settlePublicTurn())
          .catch(() => queue.notify("The scheduled linked result could not refresh yet.", "error"));
      }
      try {
        await queue.afterBoardChange();
      } catch {
        queue.notify("Approved together, but the latest Work view could not refresh yet.", "error");
      }
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "Those linked actions were not approved.", "error");
    } finally {
      setProgress(null);
    }
  };

  return (
    <article
      className={`approval-bundle ${embedded ? "approval-bundle--embedded" : ""}`}
      aria-label="Task and linked WhatsApp message approval"
      aria-busy={progress !== null}
    >
      <header className="approval-bundle__header">
        <div><span>One business intent</span><h3>Task + linked message</h3></div>
        <p>One approval · separately audited Work and Send effects</p>
      </header>
      <div className="approval-bundle__effects">
        {unit.proposals.map((proposal) => (
          <ProposalCard
            key={proposal.id}
            ref={(handle) => setMemberRef(proposal.id, handle)}
            proposal={proposal}
            board={board}
            queue={queue}
            embedded
            bundleMember
            bundleBusy={progress !== null}
            onApprovalReadyChange={setMemberReady}
          />
        ))}
      </div>
      {!unit.complete ? (
        <p className="approval-bundle__warning" role="alert">Waiting for every linked action. Nothing can be approved yet.</p>
      ) : null}
      {progress ? (
        <div className="approval-bundle__progress" role="status" aria-live="polite" aria-atomic="true">
          <LoaderCircle className="spin" size={17} aria-hidden="true" /><span>{progress}</span>
        </div>
      ) : null}
      <footer className="approval-bundle__actions">
        <button
          className="primary-button"
          type="button"
          disabled={!allReady || progress !== null}
          aria-label={`${approvalLabel} together`}
          onClick={() => void approve()}
        >
          {progress ? <LoaderCircle className="spin" size={17} aria-hidden="true" /> : <Check size={17} aria-hidden="true" />}
          {progress ?? approvalLabel}
        </button>
      </footer>
    </article>
  );
}

export const APPROVAL_BUNDLE_PROGRESS = {
  preparing: "Preparing Task + Message…",
  applying: "Applying one approval…",
  done: "Done — approval recorded."
} as const;

export function approvalBundleActionLabel(proposals: readonly AgentProposal[]): string {
  const hasTask = proposals.some((proposal) => proposal.effect.kind === "NEW_WORK" || proposal.effect.kind === "BOARD_UPDATE");
  const hasMessage = proposals.some((proposal) => proposal.effect.kind === "SEND_MESSAGE");
  return hasTask && hasMessage ? "Approve Task + Message" : "Approve linked actions";
}

export function approvalBundleSuccessNotice(proposals: readonly AgentProposal[]): string {
  const scheduled = proposals.find((proposal): proposal is AgentProposal & {
    effect: Extract<AgentProposalEffect, { kind: "SEND_MESSAGE" }>;
  } => proposal.effect.kind === "SEND_MESSAGE" && proposal.effect.schedule !== undefined);
  if (scheduled) {
    // WHY BOL-APPROVAL-BUNDLE-TRUTH-001: bundle approval grants authority but
    // the Work executor still has to succeed before the dependent Send is
    // eligible. Never present authorization as completed Work. Falsifier: this
    // notice claims the task is scheduled/applied before its proposal succeeds.
    return `Done — approval recorded. Work is applying. The message remains gated until Work succeeds, the scheduled time (${formatScheduledEffect(scheduled.effect)}) arrives, and final checks pass.`;
  }
  return "Done — approval recorded. Work and Send now have separate final safety checks; nothing is marked sent yet.";
}

const TERMINAL_EFFECT_STATES = new Set<OpsPublicProposalProjection["state"]>([
  "SUCCEEDED",
  "CANCELLED",
  "FAILED",
  "DELIVERY_UNCERTAIN"
]);

export function hasTerminalPublicEffectReceipt(
  proposal: OpsPublicProposalProjection
): proposal is OpsPublicProposalProjection & {
  effectReceipt: NonNullable<OpsPublicProposalProjection["effectReceipt"]>;
} {
  return TERMINAL_EFFECT_STATES.has(proposal.state) && proposal.effectReceipt !== undefined;
}

function publicEffectSubject(effectKind: NonNullable<OpsPublicProposalProjection["effectReceipt"]>["effectKind"]): string {
  if (effectKind === "SEND_MESSAGE") return "Message";
  if (effectKind === "BUSINESS_RULE_UPDATE") return "Manager rule";
  return "Work";
}

function publicEffectOutcome(proposal: OpsPublicProposalProjection): { label: string; text: string } {
  const message = proposal.effectReceipt?.effectKind === "SEND_MESSAGE";
  if (proposal.state === "SUCCEEDED") return {
    label: message ? "Sent" : "Changed",
    text: message
      ? "The approved message effect completed."
      : "The approved Work effect completed."
  };
  if (proposal.state === "DELIVERY_UNCERTAIN") return {
    label: "Delivery needs checking",
    text: "Delivery is uncertain. This committed message effect will not be retried."
  };
  if (proposal.state === "FAILED") return {
    label: "Stopped",
    text: "The approved effect stopped safely. Nothing will be retried from this receipt."
  };
  return {
    label: "Stopped",
    text: "The approved effect was cancelled. Nothing will be retried from this receipt."
  };
}

function ApprovalReceiptDetails({ projection }: { projection: OpsPublicProposalProjection }) {
  if (projection.approvedRevision === null && !projection.receipt) return null;
  return (
    <section className="proposal-provider-receipt" aria-label="Approval receipt">
      <span>Approval receipt</span>
      <dl>
        <div><dt>Approved revision</dt><dd>{projection.approvedRevision ?? "Not current"}</dd></div>
        {projection.receipt ? <>
          <div><dt>Receipt ID</dt><dd><code>{projection.receipt.id}</code></dd></div>
          <div><dt>Receipt status</dt><dd>{projection.receipt.status}</dd></div>
        </> : null}
      </dl>
    </section>
  );
}

function EffectReceiptDetails({ projection }: { projection: OpsPublicProposalProjection }) {
  const receipt = projection.effectReceipt;
  if (!receipt) return null;
  return (
    <section className="proposal-provider-receipt" aria-label="Effect receipt">
      <span>Effect receipt</span>
      <dl>
        <div><dt>Effect</dt><dd>{publicEffectSubject(receipt.effectKind)}</dd></div>
        <div><dt>Receipt ID</dt><dd><code>{receipt.id}</code></dd></div>
        <div><dt>Receipt status</dt><dd>{receipt.status}</dd></div>
        <div><dt>Proposal revision</dt><dd>{receipt.proposalRevision}</dd></div>
      </dl>
    </section>
  );
}

function ProviderInvocationReceipt({ receipt }: {
  receipt: NonNullable<OpsPublicProposalProjection["providerInvocation"]>;
}) {
  return (
    <section className="proposal-provider-receipt" aria-label="Provider invocation receipt">
      <span>Provider invocation receipt</span>
      <dl>
        <div><dt>One-call authority</dt><dd>{receipt.attemptCount} committed</dd></div>
        <div><dt>Invocation ID</dt><dd><code>{receipt.id}</code></dd></div>
        <div><dt>Attempt committed at</dt><dd><time dateTime={receipt.attemptCommittedAt}>{receipt.attemptCommittedAt}</time></dd></div>
        <div><dt>Provider call observation</dt><dd>{receipt.invokedAt === null ? "Not observed" : <time dateTime={receipt.invokedAt}>{receipt.invokedAt}</time>}</dd></div>
        <div><dt>Provider message ID</dt><dd>{receipt.providerMessageId ?? "Not confirmed"}</dd></div>
      </dl>
      <p>The committed one-call authority will not be retried.</p>
    </section>
  );
}

export function PublicProposalTerminalReceiptCard({ projection }: {
  projection: OpsPublicProposalProjection;
}) {
  if (!hasTerminalPublicEffectReceipt(projection)) return null;
  const subject = publicEffectSubject(projection.effectReceipt.effectKind);
  const outcome = publicEffectOutcome(projection);
  return (
    <article className={`proposal-outcome proposal-outcome--${projection.state.toLowerCase()}`} aria-label={`${subject} effect receipt: ${outcome.label}`}>
      <ShieldCheck size={18} aria-hidden="true" />
      <div>
        <span>{outcome.label}</span><strong>{subject} effect receipt</strong><p>{outcome.text}</p>
        <small>Durable terminal result · {projection.state}</small>
        <ApprovalReceiptDetails projection={projection} />
        <EffectReceiptDetails projection={projection} />
        {projection.providerInvocation ? <ProviderInvocationReceipt receipt={projection.providerInvocation} /> : null}
      </div>
    </article>
  );
}

export function PublicProposalTerminalReceiptUnit({ projections }: {
  projections: readonly OpsPublicProposalProjection[];
}) {
  if (projections.length === 0 || !projections.every(hasTerminalPublicEffectReceipt)) return null;
  if (projections.length === 1) return <PublicProposalTerminalReceiptCard projection={projections[0]!} />;
  return (
    <article className="approval-bundle approval-bundle--outcome" aria-label="Task and linked message terminal receipts">
      <header className="approval-bundle__header"><div><span>Approved together</span><h3>Task + linked message</h3></div><p>Durable Work and Send receipts</p></header>
      <div className="approval-bundle__effects">{projections.map((projection) => (
        <PublicProposalTerminalReceiptCard key={projection.id} projection={projection} />
      ))}</div>
    </article>
  );
}

export function ApprovalUnitOutcomeCard({ unit, publicProposals = [], actions }: {
  unit: ApprovalUnit;
  publicProposals?: readonly OpsPublicProposalProjection[];
  actions?: ProposalOutcomeActions;
}) {
  const publicProposalById = new Map(publicProposals.map((proposal) => [proposal.id, proposal]));
  if (!unit.bundleId) {
    const proposal = unit.proposals[0];
    return proposal ? <ProposalOutcomeCard
      proposal={proposal}
      {...(publicProposalById.get(proposal.id) ? { projection: publicProposalById.get(proposal.id)! } : {})}
      {...(actions ? { actions } : {})}
    /> : null;
  }
  return (
    <article className="approval-bundle approval-bundle--outcome" aria-label="Task and linked message outcome">
      <header className="approval-bundle__header"><div><span>Approved together</span><h3>Task + linked message</h3></div><p>Separate Work and Send audit results</p></header>
      <div className="approval-bundle__effects">{unit.proposals.map((proposal) => <ProposalOutcomeCard
        key={proposal.id}
        proposal={proposal}
        {...(publicProposalById.get(proposal.id) ? { projection: publicProposalById.get(proposal.id)! } : {})}
        {...(actions ? { actions } : {})}
      />)}</div>
    </article>
  );
}

export function proposalActionProgressLabel(effect: AgentProposalEffect): string {
  if (effect.kind === "SEND_MESSAGE") return effect.schedule ? "Scheduling…" : "Preparing to send…";
  if (effect.kind === "NEW_WORK") return "Adding task…";
  if (effect.kind === "BOARD_UPDATE") return "Updating Work…";
  return "Saving rule…";
}

// WHY [BOL-APPROVAL-EFFECT-IDENTITY-001]: one review stream may contain Work
// changes and outbound messages. A server-declared bundle may authorize them
// together, but their effects, receipts, and audits remain distinct. Name each
// literal effect inside that one unit—and preserve the same label on singles.
// Falsifier: the reviewer cannot distinguish ADD TASK from SEND MESSAGE before
// the one bundle action, or an unbundled proposal looks combined.
export function proposalEffectLabel(effect: AgentProposalEffect): string {
  if (effect.kind === "SEND_MESSAGE") return effect.schedule ? "SCHEDULE MESSAGE" : "SEND MESSAGE";
  if (effect.kind === "NEW_WORK") return "ADD TASK";
  if (effect.kind === "BOARD_UPDATE") return "UPDATE TASK";
  return "SAVE RULE";
}

function channelLabel(channel: string): string {
  const known: Readonly<Record<string, string>> = {
    WHATSAPP: "WhatsApp",
    INSTAGRAM: "Instagram",
    THREADS: "Threads",
    EMAIL: "Email"
  };
  if (known[channel]) return known[channel];
  return channel.toLocaleLowerCase().replaceAll("_", " ").replace(/\b\p{L}/gu, (letter) => letter.toLocaleUpperCase());
}

function formatEvidenceTime(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date)
    : "";
}

type ProposalOutcomeState = AgentProposal["state"] | "PENDING";

async function waitForProposalOutcome(id: string): Promise<ProposalOutcomeState> {
  // WHY [BOL-APPROVAL-OUTCOME-001]: approval and execution are separate safety
  // boundaries. Removing the card immediately made a successful task approval
  // look broken and could imply that a message sent when it had only queued.
  // Poll the authenticated exact-ID endpoint; neither a paged queue nor bounded
  // history may decide that the named outcome vanished. Falsifier: the UI says
  // added/sent before SUCCEEDED, or a page boundary hides a terminal stop.
  return (await waitForExactProposalOutcome(id))?.state ?? "PENDING";
}

export function proposalConsequence(effect: AgentProposalEffect, destination: string, targetLabel: string, targetRequired: boolean): string {
  if (effect.kind === "NEW_WORK") return "add this task to Work and Calendar. No WhatsApp message will be sent.";
  if (effect.kind === "BOARD_UPDATE") return "update the same task in Work and Calendar. No WhatsApp message will be sent.";
  if (effect.kind === "BUSINESS_RULE_UPDATE") return "save this manager rule as a new version of the business rules. No WhatsApp message will be sent and no task is created.";
  const person = targetRequired ? ` to ${targetLabel}` : "";
  const file = effect.attachment ? ` and ${effect.attachment.displayName}` : "";
  if (effect.schedule) {
    return `schedule the message${file}${person} in ${destination} for ${formatScheduledEffect(effect)}. It will not send now.`;
  }
  return `send the message${file}${person} in ${destination}, after one final live route and file check.`;
}

export function formatScheduledEffect(effect: Extract<AgentProposalEffect, { kind: "SEND_MESSAGE" }>): string {
  if (!effect.schedule) return "the selected time";
  const label = new Intl.DateTimeFormat(undefined, {
    timeZone: effect.schedule.timeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(effect.schedule.executeAt));
  return `${label} · ${effect.schedule.timeZone} · UTC${effect.schedule.utcOffset}`;
}

export function formatReviewedSchedule(localDateTime: string, timeZone: string, utcOffset: string): string {
  const wallTime = localDateTime.trim().replace("T", " ") || "the selected time";
  return `${wallTime} · ${timeZone} · UTC${utcOffset}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${Math.ceil(bytes / 1_024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function proposalOutcomeTone(state: ProposalOutcomeState): "success" | "error" {
  return ["CANCELLED", "FAILED", "DELIVERY_UNCERTAIN"].includes(state) ? "error" : "success";
}

export function proposalOutcomeNotice(proposal: AgentProposal, state: ProposalOutcomeState): string {
  const message = proposal.effect.kind === "SEND_MESSAGE";
  const schedule = proposal.effect.kind === "SEND_MESSAGE" ? proposal.effect.schedule : undefined;
  if (schedule && state === "APPROVED") return `Scheduled for ${formatScheduledEffect(proposal.effect as Extract<AgentProposalEffect, { kind: "SEND_MESSAGE" }>)}. Not sent yet.`;
  if (state === "SUCCEEDED") {
    if (message) return `Sent to ${proposal.destinationLabel ?? "the verified WhatsApp destination"}.`;
    if (proposal.effect.kind === "NEW_WORK") return "Task added to Work and Calendar. Nothing was sent on WhatsApp.";
    if (proposal.effect.kind === "BOARD_UPDATE") return "Work and Calendar updated. Nothing was sent on WhatsApp.";
    return "Manager rule saved. Nothing was sent on WhatsApp.";
  }
  if (state === "DELIVERY_UNCERTAIN") return "WhatsApp did not confirm delivery. Check Activity; this send will not be retried.";
  if (schedule && (state === "CANCELLED" || state === "FAILED")) return "Scheduled message stopped. Nothing was sent by this schedule.";
  if (state === "CANCELLED" || state === "FAILED") return message
    ? "Safety check stopped the message. Nothing new was sent."
    : "Safety check stopped this change. Work and Calendar were not changed.";
  return message
    ? "Approved. The final live route check is still running; nothing is marked sent yet."
    : "Approved. The final safety check is still running; no change is claimed yet.";
}

export function ProposalOutcomeCard({ proposal, projection, actions }: {
  proposal: AgentProposal;
  projection?: OpsPublicProposalProjection;
  actions?: ProposalOutcomeActions;
}) {
  const isMessage = proposal.effect.kind === "SEND_MESSAGE";
  const isScheduled = proposal.effect.kind === "SEND_MESSAGE" && proposal.effect.schedule !== undefined;
  const approvedScheduledBundle = isScheduled
    && proposal.state === "APPROVED"
    && proposal.approvalBundle !== null
    && actions !== undefined;
  const schedule = proposal.effect.kind === "SEND_MESSAGE" ? proposal.effect.schedule : undefined;
  const messageDraftLimit = proposal.type ? agentProposalMessageDraftMaxLength(proposal.type) : 300;
  const [scheduleLocalDateTime, setScheduleLocalDateTime] = useState(schedule?.localDateTime ?? "");
  const [message, setMessage] = useState(proposal.messageDraft ?? "");
  const [scheduleAction, setScheduleAction] = useState<"EDIT" | "CANCEL" | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [committedAction, setCommittedAction] = useState<"EDIT" | "CANCEL" | null>(null);
  useEffect(() => {
    setScheduleLocalDateTime(schedule?.localDateTime ?? "");
    setMessage(proposal.messageDraft ?? "");
    setScheduleAction(null);
    setConfirmingCancel(false);
    setCommittedAction(null);
  }, [proposal.id, proposal.messageDraft, proposal.currentRevision, proposal.state, proposal.version, schedule?.localDateTime]);
  const setBoundedScheduleMessage = (value: string) => {
    if (!proposal.type || isAgentProposalMessageDraftWithinBound(proposal.type, value)) setMessage(value);
  };
  const scheduleDirty = schedule !== undefined
    && (scheduleLocalDateTime !== schedule.localDateTime || message !== (proposal.messageDraft ?? ""));
  const providerReceipt = isMessage
    ? projection ? projection.providerInvocation : proposal.providerInvocation
    : undefined;
  const label = isScheduled && proposal.state === "APPROVED"
    ? "Scheduled"
    : proposal.state === "SUCCEEDED"
    ? isMessage ? "Sent" : "Changed"
    : ["APPROVED", "EXECUTING"].includes(proposal.state)
      ? "Final safety check"
      : proposal.state === "DELIVERY_UNCERTAIN"
        ? "Delivery needs checking"
        : proposal.state === "SNOOZED"
          ? "Snoozed"
          : "Stopped";
  const text = proposal.state === "REJECTED"
    ? "Rejected. Nothing changed or sent."
    : proposal.state === "SNOOZED"
      ? "Snoozed. Nothing changed or sent."
      : proposal.state === "EXPIRED"
        ? "This approval expired. Nothing changed or sent."
      : proposalOutcomeNotice(proposal, proposal.state);

  const refreshCommittedMutation = async (
    notice: string,
    refreshFailure: string
  ) => {
    actions!.notify(notice);
    try {
      await actions!.afterMutation();
    } catch {
      actions!.notify(refreshFailure, "error");
    }
  };

  const editApprovedSchedule = async () => {
    if (!approvedScheduledBundle || !schedule || !scheduleDirty
      || !scheduleLocalDateTime || !message.trim()) return;
    setScheduleAction("EDIT");
    try {
      await api.editScheduledProposal(proposal.id, {
        expectedVersion: proposal.version,
        revision: proposal.currentRevision,
        localDateTime: scheduleLocalDateTime,
        messageDraft: message
      });
      // The API/database transaction revokes both old jobs and both receipts
      // before either linked member returns to Review. A refresh failure after
      // that commit must not turn this into another edit attempt.
      setCommittedAction("EDIT");
      await refreshCommittedMutation(
        "Task + Message returned to Approvals for one fresh decision. Nothing was sent.",
        "The edit saved, but the latest linked approval could not refresh yet."
      );
    } catch (error) {
      actions.notify(error instanceof Error ? error.message : "That scheduled Task + Message was not edited.", "error");
    } finally {
      setScheduleAction(null);
    }
  };

  const cancelApprovedSchedule = async () => {
    if (!approvedScheduledBundle) return;
    setScheduleAction("CANCEL");
    try {
      await api.cancelScheduledProposal(
        proposal.id,
        proposal.version,
        proposal.currentRevision
      );
      setCommittedAction("CANCEL");
      await refreshCommittedMutation(
        "Task + scheduled Message cancelled. Nothing was sent.",
        "The cancellation saved, but the latest linked outcome could not refresh yet."
      );
    } catch (error) {
      actions.notify(error instanceof Error ? error.message : "That scheduled Task + Message was not cancelled.", "error");
    } finally {
      setScheduleAction(null);
      setConfirmingCancel(false);
    }
  };

  return (
    <article className={`proposal-outcome proposal-outcome--${proposal.state.toLowerCase()}`} aria-label={`${proposal.title}: ${label}`} aria-busy={scheduleAction !== null}>
      <ShieldCheck size={18} aria-hidden="true" />
      <div>
        <span>{label}</span><strong>{proposal.title}</strong><p>{text}</p><small>{isMessage ? proposal.destinationLabel ?? "Verified destination pending" : "Board and Calendar"}</small>
        {projection ? <>
          {/* WHY [BOL-OPS-APPROVAL-RECEIPT-001]: an outcome label cannot prove
              which immutable revision was approved or whether its receipt remains
              issued, consumed, or revoked. Render only the public-turn ledger
              projection; never infer receipt state from the proposal label. */}
          <ApprovalReceiptDetails projection={projection} />
          {/* WHY [BOL-J2-PUBLIC-EFFECT-RECEIPT-001]: /review is a mutable,
              bounded convenience view. The public turn must retain the exact
              approved-revision executor job identity and terminal status so a
              completed or uncertain effect cannot reload as merely approved.
              Falsifier: the terminal card lacks this ID/status, or substitutes
              a newer /review proposal for the public receipt. */}
          <EffectReceiptDetails projection={projection} />
        </> : null}
        {providerReceipt ? (
          // WHY [BOL-J2-PROVIDER-RECEIPT-001]: terminal state alone cannot show
          // whether the one provider-call authority was committed or observed.
          // A public projection never falls back to mutable /review provider
          // fields; a missing invokedAt remains unobserved and never authorizes
          // retry. Falsifier: reload hides or replaces the public invocation ID,
          // or a null field becomes a claimed call or delivery confirmation.
          <ProviderInvocationReceipt receipt={providerReceipt} />
        ) : null}
        {approvedScheduledBundle ? committedAction ? (
          <div className="approval-action-progress" role="status">
            <ShieldCheck size={17} aria-hidden="true" />
            <span>{committedAction === "EDIT"
              ? "Saved for one fresh Task + Message review. Nothing was sent."
              : "Task + Message cancelled. Nothing was sent."}</span>
          </div>
        ) : <>
          {/* WHY [BOL-SCHEDULE-BUNDLE-OUTCOME-CONTROLS-001]: approval removes
              both linked rows from the active Review queue, but a future send
              remains operator-editable until its shared cutoff. Keep Edit and
              Cancel on the exact outcome. Both calls carry the displayed CAS;
              the database revokes both bundle jobs atomically, and this screen
              never offers retry for an invoked/uncertain send. Falsifier: an
              APPROVED bundle has no controls, or either control leaves one old
              Task/Message authority live. */}
          <section className="proposal-schedule" aria-label="Edit approved Task and scheduled Message">
            <label><span>Send at</span><input type="datetime-local" step={1} value={scheduleLocalDateTime} disabled={scheduleAction !== null} onChange={(event) => setScheduleLocalDateTime(event.target.value)} /></label>
            <div><span>Timezone</span><strong>{schedule!.timeZone} · UTC{schedule!.utcOffset}</strong></div>
            <p><Clock3 size={15} aria-hidden="true" />Editing revokes the whole prior Task + Message approval. Both return for one fresh review. Nothing is sent now.</p>
          </section>
          <label className="message-draft-field"><span>Message</span><textarea rows={3} maxLength={messageDraftLimit * 2} value={message} disabled={scheduleAction !== null} onChange={(event) => setBoundedScheduleMessage(event.target.value)} /><small>{unicodeScalarLength(message) ?? 0}/{messageDraftLimit}</small></label>
          <footer className="review-card__actions">
            <button className="primary-button" type="button" disabled={scheduleAction !== null || !scheduleDirty || !scheduleLocalDateTime || !message.trim()} onClick={() => void editApprovedSchedule()}>{scheduleAction === "EDIT" ? <LoaderCircle className="spin" size={17} aria-hidden="true" /> : <FilePenLine size={16} aria-hidden="true" />}{scheduleAction === "EDIT" ? "Saving for review…" : "Save edits for review"}</button>
            <button className="quiet-review-action" type="button" disabled={scheduleAction !== null} onClick={() => setConfirmingCancel(true)}><X size={16} aria-hidden="true" />Cancel Task + Message</button>
          </footer>
          {confirmingCancel ? <div className="reject-row" role="group" aria-label="Confirm scheduled Task and Message cancellation"><button type="button" disabled={scheduleAction !== null} onClick={() => void cancelApprovedSchedule()}>{scheduleAction === "CANCEL" ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <X size={16} aria-hidden="true" />}{scheduleAction === "CANCEL" ? "Cancelling…" : "Confirm cancel"}</button><button type="button" disabled={scheduleAction !== null} onClick={() => setConfirmingCancel(false)}>Keep schedule</button></div> : null}
        </> : null}
      </div>
    </article>
  );
}

function RecentAgentActivity({ history }: { history: AgentProposal[] }) {
  const completed = history.filter((proposal) => proposal.state === "SUCCEEDED").length;
  const sent = history.filter((proposal) => proposal.state === "SUCCEEDED" && proposal.effect.kind === "SEND_MESSAGE").length;
  const changed = completed - sent;
  const scheduled = history.filter((proposal) => proposal.state === "APPROVED" && proposal.effect.kind === "SEND_MESSAGE" && proposal.effect.schedule !== undefined).length;
  const stopped = history.filter((proposal) => ["CANCELLED", "REJECTED", "EXPIRED", "FAILED"].includes(proposal.state)).length;
  return <details className="recent-agent-activity"><summary>Recent activity <span>{changed} changed · {sent} sent · {scheduled} scheduled · {stopped} stopped</span></summary><ol>{history.slice(0, 6).map((proposal) => { const isSend = proposal.effect.kind === "SEND_MESSAGE"; const isScheduled = proposal.effect.kind === "SEND_MESSAGE" && proposal.effect.schedule !== undefined; return <li key={proposal.id}><div><strong>{proposal.workItemTitle ?? proposal.title}</strong><small>{isSend ? proposal.destinationLabel ?? "Route not confirmed" : "Work and Calendar"}</small></div><span>{isScheduled && proposal.state === "APPROVED" ? "Scheduled" : proposal.state === "SUCCEEDED" ? isSend ? "Sent" : "Changed" : proposal.state === "DELIVERY_UNCERTAIN" ? "Check delivery" : ["APPROVED", "EXECUTING"].includes(proposal.state) ? "Checking" : "Stopped"}</span></li>; })}</ol></details>;
}

function ProposalPreview({ type, board, effect, message }: {
  type: AgentProposal["type"];
  board: BoardSnapshot;
  effect: AgentProposalEffect;
  message: string;
}) {
  if (effect.kind === "SEND_MESSAGE") {
    const target = board.members.find((member) => member.id === effect.mentionMemberIds[0]);
    const targetText = requiresNotifyTarget(type)
      ? target?.whatsAppLinked ? `Notifies ${memberLabel(board.members, target)}` : target ? `Match ${memberLabel(board.members, target)} in WhatsApp` : "Choose who to notify"
      : null;
    return <div className="message-preview"><MessageCircleMore size={18} /><div className="message-preview__body"><p>{message}</p>{targetText ? <small className={target?.whatsAppLinked ? "message-target" : "message-target message-target--missing"}>{targetText}</small> : null}</div></div>;
  }
  if (effect.kind === "NEW_WORK") {
    // WHY [BOL-INLINE-WORK-DATE-001]: the proposal already retained the exact
    // deadline, but the inline card hid it. An operator could therefore approve
    // a calendar action without seeing when it would occur. Keep the compact
    // card while showing every decision-critical owner/deadline value before
    // approval. Falsifier: the stored dueAt/assignee is absent or differs here.
    const owner = effect.draft.assigneeId
      ? board.members.find((member) => member.id === effect.draft.assigneeId)?.displayName ?? "Unknown owner"
      : "Unassigned";
    const due = effect.draft.dueAt
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: board.workspace.timezone,
          dateStyle: "medium",
          timeStyle: "short"
        }).format(new Date(effect.draft.dueAt))
      : "No deadline";
    const scheduled = effect.draft.workKind === "ENGAGEMENT" && effect.draft.scheduledStartAt
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: board.workspace.timezone,
          dateStyle: "medium",
          ...(effect.draft.scheduledAllDay ? {} : { timeStyle: "short" as const })
        }).format(new Date(effect.draft.scheduledStartAt))
      : null;
    return <div className="effect-preview"><strong>{scheduled ? "New engagement" : "New work"}</strong><span>{effect.draft.title}</span><small>{statusLabel(effect.draft.status)} · {effect.draft.priority.toLowerCase()} · {owner}{scheduled ? ` · scheduled ${scheduled}` : ""} · due {due}</small></div>;
  }
  if (effect.kind === "BUSINESS_RULE_UPDATE") {
    const lines = ruleBoxLines(effect.policy);
    const detail = [lines.detail, effect.policy.checkpointLeadMinutes === null ? null : `${effect.policy.checkpointLeadMinutes / 1_440} days before due`].filter((part): part is string => Boolean(part)).join(" · ");
    return <div className="effect-preview"><strong>Manager rule</strong><span>{lines.title}</span>{detail ? <small>{detail}</small> : null}</div>;
  }
  const changes = boardChangePreviewRows(board, effect);
  return <div className="effect-preview"><strong>Proposed change</strong><ul>{changes.map((change) => <li key={change.field}><span>{change.label}</span><div className="effect-preview__diff"><small><em>Current</em>{change.current}</small><b><em>After approval</em>{change.next}</b></div></li>)}{effect.note ? <li><span>Note</span><b>{effect.note}</b></li> : null}</ul></div>;
}

export type BoardChangePreviewRow = Readonly<{
  field: string;
  label: string;
  current: string;
  next: string;
}>;

// WHY [BOL-INLINE-WORK-DIFF-001]: a proposed Calendar move is safe to approve
// only when its old and new values are derived from the live Work item beside
// the exact proposed patch. Never cache a second Calendar copy or show the new
// date as though it already happened. Falsifier: the preview omits either side,
// reads a different Work item, or approval changes anything outside that item.
export function boardChangePreviewRows(
  board: BoardSnapshot,
  effect: Extract<AgentProposalEffect, { kind: "BOARD_UPDATE" }>
): BoardChangePreviewRow[] {
  const current = board.workItems.find((item) => item.id === effect.workItemId);
  const currentValues = current as unknown as Record<string, unknown> | undefined;
  return Object.entries(effect.changes).map(([field, next]) => ({
    field,
    label: fieldLabel(field),
    current: current
      ? displayValue(board, field, currentValues?.[field], "Not set")
      : "Live item unavailable",
    next: displayValue(board, field, next, "Clear")
  }));
}

function ProposalEditor({ type, board, effect, setEffect, message, setMessage, completeTaskFields = false }: {
  type: AgentProposal["type"];
  board: BoardSnapshot;
  effect: AgentProposalEffect;
  setEffect: (effect: AgentProposalEffect) => void;
  message: string;
  setMessage: (message: string) => void;
  completeTaskFields?: boolean;
}) {
  const messageDraftLimit = agentProposalMessageDraftMaxLength(type);
  const setBoundedMessage = (value: string) => {
    if (isAgentProposalMessageDraftWithinBound(type, value)) setMessage(value);
  };
  if (effect.kind === "SEND_MESSAGE") {
    if (!requiresNotifyTarget(type)) return <label className="proposal-editor"><span>Message</span><textarea rows={3} maxLength={messageDraftLimit * 2} value={message} onChange={(event) => setBoundedMessage(event.target.value)} /><small>{unicodeScalarLength(message) ?? 0}/{messageDraftLimit}</small></label>;
    return <div className="proposal-editor-grid">
      <label className="proposal-editor-grid__wide"><span>Message</span><textarea rows={3} maxLength={messageDraftLimit * 2} value={message} onChange={(event) => setBoundedMessage(event.target.value)} /></label>
      <label className="proposal-editor-grid__wide"><span>Notify</span><select value={effect.mentionMemberIds[0] ?? ""} onChange={(event) => setEffect({ ...effect, mentionMemberIds: event.target.value ? [event.target.value] : [] })}><option value="">Choose person</option>{board.members.map((member) => <option value={member.id} key={member.id} disabled={member.whatsAppLinked !== true}>{memberLabel(board.members, member)}{member.whatsAppLinked === true ? "" : " — match in WhatsApp"}</option>)}</select></label>
    </div>;
  }
  if (effect.kind === "NEW_WORK") {
    const update = (draft: AgentProposalEffect & { kind: "NEW_WORK" }) => setEffect(draft);
    const projects = effect.draft.clientId
      ? board.projects.filter((project) => project.clientId === effect.draft.clientId)
      : board.projects;
    return <div className="proposal-editor-grid">
      <label className="proposal-editor-grid__wide"><span>Work</span><input maxLength={240} value={effect.draft.title} onChange={(event) => update({ ...effect, draft: { ...effect.draft, title: event.target.value } })} /></label>
      <label><span>Type</span><select value={effect.draft.workKind ?? "TASK"} onChange={(event) => { const workKind = event.target.value as "TASK" | "ENGAGEMENT"; update({ ...effect, draft: { ...effect.draft, workKind, ...(workKind === "TASK" ? { scheduledStartAt: null, scheduledEndAt: null, scheduledAllDay: false } : { taskStartAt: null, taskEndAt: null }) } }); }}><option value="TASK">Task</option><option value="ENGAGEMENT">Meeting, shoot, or event</option></select></label>
      <label><span>Client</span><select value={effect.draft.clientId ?? ""} onChange={(event) => { const clientId = event.target.value || null; const projectStillFits = !effect.draft.projectId || board.projects.some((project) => project.id === effect.draft.projectId && (!clientId || project.clientId === clientId)); update({ ...effect, draft: { ...effect.draft, clientId, projectId: projectStillFits ? effect.draft.projectId : null } }); }}><option value="">No client</option>{board.clients.map((client) => <option value={client.id} key={client.id}>{client.name}</option>)}</select></label>
      <label><span>Project</span><select value={effect.draft.projectId ?? ""} onChange={(event) => { const projectId = event.target.value || null; const project = board.projects.find((item) => item.id === projectId); update({ ...effect, draft: { ...effect.draft, projectId, clientId: project?.clientId ?? effect.draft.clientId } }); }}><option value="">No project</option>{projects.map((project) => <option value={project.id} key={project.id}>{project.name}</option>)}</select></label>
      <label><span>Status</span><select value={effect.draft.status} onChange={(event) => update({ ...effect, draft: { ...effect.draft, status: event.target.value as typeof effect.draft.status } })}>{Object.entries(WORK_STATUS_LABELS).filter(([value]) => value !== "ARCHIVED").map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label><span>Priority</span><select value={effect.draft.priority} onChange={(event) => update({ ...effect, draft: { ...effect.draft, priority: event.target.value as typeof effect.draft.priority } })}><option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></label>
      <label><span>Assigned by</span><select value={effect.draft.assignedByMemberId ?? ""} onChange={(event) => update({ ...effect, draft: { ...effect.draft, assignedByMemberId: event.target.value || null } })}><option value="">Not recorded</option>{board.members.map((member) => <option value={member.id} key={member.id}>{memberLabel(board.members, member)}</option>)}</select></label>
      <label><span>Assigned to / person</span><select value={effect.draft.assigneeId ?? ""} onChange={(event) => update({ ...effect, draft: { ...effect.draft, assigneeId: event.target.value || null } })}><option value="">Unassigned</option>{board.members.map((member) => <option value={member.id} key={member.id}>{memberLabel(board.members, member)}</option>)}</select></label>
      <label><span>Department</span><input maxLength={160} value={effect.draft.department ?? ""} onChange={(event) => update({ ...effect, draft: { ...effect.draft, department: event.target.value || null } })} /></label>
      <label><span>Assignment date &amp; time</span><input type="datetime-local" value={toLocalDateTime(effect.draft.assignedAt, board.workspace.timezone)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, assignedAt: event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null } })} /></label>
      <label><span>Deadline</span><input type="datetime-local" value={toLocalDateTime(effect.draft.dueAt, board.workspace.timezone)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, dueAt: event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null } })} /></label>
      {(effect.draft.workKind ?? "TASK") === "TASK" ? <><label><span>Timeline start</span><input type="date" value={toLocalDateTime(effect.draft.taskStartAt, board.workspace.timezone).slice(0, 10)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, taskStartAt: event.target.value ? fromLocalDateTime(`${event.target.value}T00:00`, board.workspace.timezone) : null } })} /></label><label><span>Timeline end</span><input type="date" value={toLocalDateTime(effect.draft.taskEndAt, board.workspace.timezone).slice(0, 10)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, taskEndAt: event.target.value ? fromLocalDateTime(`${event.target.value}T00:00`, board.workspace.timezone) : null } })} /></label></> : null}
      {effect.draft.workKind === "ENGAGEMENT" ? <><label><span>Starts</span><input type="datetime-local" value={toLocalDateTime(effect.draft.scheduledStartAt, board.workspace.timezone)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, scheduledStartAt: event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null } })} /></label><label><span>Ends</span><input type="datetime-local" disabled={effect.draft.scheduledAllDay} value={toLocalDateTime(effect.draft.scheduledEndAt, board.workspace.timezone)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, scheduledEndAt: event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null } })} /></label><label><span>Time</span><select value={effect.draft.scheduledAllDay ? "ALL_DAY" : "TIMED"} onChange={(event) => update({ ...effect, draft: { ...effect.draft, scheduledAllDay: event.target.value === "ALL_DAY", scheduledEndAt: event.target.value === "ALL_DAY" ? null : effect.draft.scheduledEndAt } })}><option value="TIMED">Timed</option><option value="ALL_DAY">All day</option></select></label></> : null}
      {"nextCheckpointAt" in effect.draft ? <label><span>Next check</span><input type="datetime-local" value={toLocalDateTime(effect.draft.nextCheckpointAt, board.workspace.timezone)} onChange={(event) => update({ ...effect, draft: { ...effect.draft, nextCheckpointAt: event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null } })} /></label> : null}
      <label className="proposal-editor-grid__wide"><span>Task description</span><textarea rows={2} maxLength={10_000} value={effect.draft.description} onChange={(event) => update({ ...effect, draft: { ...effect.draft, description: event.target.value } })} /></label>
      <label className="proposal-editor-grid__wide"><span>Files location / attachments</span><input maxLength={4_000} value={effect.draft.filesReference ?? ""} placeholder="HTTP(S) link or plain location" onChange={(event) => update({ ...effect, draft: { ...effect.draft, filesReference: event.target.value || null } })} /></label>
      <label className="proposal-editor-grid__wide"><span>Review / feedback location</span><input maxLength={4_000} value={effect.draft.reviewFeedbackReference ?? ""} placeholder="HTTP(S) link or plain location" onChange={(event) => update({ ...effect, draft: { ...effect.draft, reviewFeedbackReference: event.target.value || null } })} /></label>
    </div>;
  }
  if (effect.kind === "BUSINESS_RULE_UPDATE") {
    const update = (policy: typeof effect.policy) => setEffect({ ...effect, policy });
    return <div className="proposal-editor-grid"><label className="proposal-editor-grid__wide"><span>Rule</span><input maxLength={500} value={effect.policy.title} onChange={(event) => update({ ...effect.policy, title: event.target.value })} /></label><label className="proposal-editor-grid__wide"><span>When it applies</span><textarea rows={2} maxLength={500} value={effect.policy.appliesWhen} onChange={(event) => update({ ...effect.policy, appliesWhen: event.target.value })} /></label><label className="proposal-editor-grid__wide"><span>Required action</span><textarea rows={2} maxLength={500} value={effect.policy.requiredAction} onChange={(event) => update({ ...effect.policy, requiredAction: event.target.value })} /></label><label><span>Days before due</span><input type="number" min={0} max={70} step={1} value={effect.policy.checkpointLeadMinutes === null ? "" : effect.policy.checkpointLeadMinutes / 1_440} onChange={(event) => update({ ...effect.policy, checkpointLeadMinutes: event.target.value === "" ? null : Math.round(Number(event.target.value) * 1_440) })} /></label></div>;
  }
  return <BoardPatchEditor board={board} effect={effect} setEffect={setEffect} completeTaskFields={completeTaskFields} />;
}

function BoardPatchEditor({ board, effect, setEffect, completeTaskFields }: {
  board: BoardSnapshot;
  effect: Extract<AgentProposalEffect, { kind: "BOARD_UPDATE" }>;
  setEffect: (effect: AgentProposalEffect) => void;
  completeTaskFields: boolean;
}) {
  const change = (field: string, value: unknown) => setEffect({ ...effect, changes: { ...effect.changes, [field]: value } });
  const current = board.workItems.find((item) => item.id === effect.workItemId);
  const value = (field: keyof typeof effect.changes, fallback: unknown) => Object.prototype.hasOwnProperty.call(effect.changes, field)
    ? effect.changes[field]
    : fallback;
  const effectiveWorkKind = value("workKind", current?.workKind ?? "TASK") as "TASK" | "ENGAGEMENT";
  return <div className="proposal-editor-grid">
    {completeTaskFields || "title" in effect.changes ? <label className="proposal-editor-grid__wide"><span>Work</span><input value={String(value("title", current?.title) ?? "")} onChange={(event) => change("title", event.target.value)} /></label> : null}
    {"status" in effect.changes ? <label><span>Status</span><select value={effect.changes.status} onChange={(event) => change("status", event.target.value)}>{Object.entries(WORK_STATUS_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label> : null}
    {"priority" in effect.changes ? <label><span>Priority</span><select value={effect.changes.priority} onChange={(event) => change("priority", event.target.value)}><option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></label> : null}
    {completeTaskFields || "assignedByMemberId" in effect.changes ? <label><span>Assigned by</span><select value={String(value("assignedByMemberId", current?.assignedByMemberId) ?? "")} onChange={(event) => change("assignedByMemberId", event.target.value || null)}><option value="">Not recorded</option>{board.members.map((member) => <option value={member.id} key={member.id}>{memberLabel(board.members, member)}</option>)}</select></label> : null}
    {completeTaskFields || "assigneeId" in effect.changes ? <label><span>Assigned to / person</span><select value={String(value("assigneeId", current?.assigneeId) ?? "")} onChange={(event) => change("assigneeId", event.target.value || null)}><option value="">Unassigned</option>{board.members.map((member) => <option value={member.id} key={member.id}>{memberLabel(board.members, member)}</option>)}</select></label> : null}
    {completeTaskFields || "department" in effect.changes ? <label><span>Department</span><input maxLength={160} value={String(value("department", current?.department) ?? "")} onChange={(event) => change("department", event.target.value || null)} /></label> : null}
    {completeTaskFields || "assignedAt" in effect.changes ? <label><span>Assignment date &amp; time</span><input type="datetime-local" value={toLocalDateTime(value("assignedAt", current?.assignedAt) as string | null | undefined, board.workspace.timezone)} onChange={(event) => change("assignedAt", event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null)} /></label> : null}
    {"clientId" in effect.changes ? <label><span>Client</span><select value={effect.changes.clientId ?? ""} onChange={(event) => change("clientId", event.target.value || null)}><option value="">No client</option>{board.clients.map((client) => <option value={client.id} key={client.id}>{client.name}</option>)}</select></label> : null}
    {completeTaskFields || "dueAt" in effect.changes ? <label><span>Deadline</span><input type="datetime-local" value={toLocalDateTime(value("dueAt", current?.dueAt) as string | null | undefined, board.workspace.timezone)} onChange={(event) => change("dueAt", event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null)} /></label> : null}
    {"workKind" in effect.changes ? <label><span>Type</span><select value={effectiveWorkKind} onChange={(event) => { const workKind = event.target.value as "TASK" | "ENGAGEMENT"; setEffect({ ...effect, changes: { ...effect.changes, workKind, ...(workKind === "TASK" ? { scheduledStartAt: null, scheduledEndAt: null, scheduledAllDay: false } : { taskStartAt: null, taskEndAt: null }) } }); }}><option value="TASK">Task</option><option value="ENGAGEMENT">Meeting, shoot, or event</option></select></label> : null}
    {(completeTaskFields || "taskStartAt" in effect.changes || "taskEndAt" in effect.changes) && effectiveWorkKind === "TASK" ? <><label><span>Timeline start</span><input type="date" value={toLocalDateTime(value("taskStartAt", current?.taskStartAt) as string | null | undefined, board.workspace.timezone).slice(0, 10)} onChange={(event) => change("taskStartAt", event.target.value ? fromLocalDateTime(`${event.target.value}T00:00`, board.workspace.timezone) : null)} /></label><label><span>Timeline end</span><input type="date" value={toLocalDateTime(value("taskEndAt", current?.taskEndAt) as string | null | undefined, board.workspace.timezone).slice(0, 10)} onChange={(event) => change("taskEndAt", event.target.value ? fromLocalDateTime(`${event.target.value}T00:00`, board.workspace.timezone) : null)} /></label></> : null}
    {"scheduledStartAt" in effect.changes && effectiveWorkKind === "ENGAGEMENT" ? <label><span>Starts</span><input type="datetime-local" value={toLocalDateTime(effect.changes.scheduledStartAt, board.workspace.timezone)} onChange={(event) => change("scheduledStartAt", event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null)} /></label> : null}
    {"scheduledEndAt" in effect.changes && effectiveWorkKind === "ENGAGEMENT" ? <label><span>Ends</span><input type="datetime-local" disabled={effect.changes.scheduledAllDay === true} value={toLocalDateTime(effect.changes.scheduledEndAt, board.workspace.timezone)} onChange={(event) => change("scheduledEndAt", event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null)} /></label> : null}
    {"scheduledAllDay" in effect.changes && effectiveWorkKind === "ENGAGEMENT" ? <label><span>Time</span><select value={effect.changes.scheduledAllDay ? "ALL_DAY" : "TIMED"} onChange={(event) => setEffect({ ...effect, changes: { ...effect.changes, scheduledAllDay: event.target.value === "ALL_DAY", ...(event.target.value === "ALL_DAY" ? { scheduledEndAt: null } : {}) } })}><option value="TIMED">Timed</option><option value="ALL_DAY">All day</option></select></label> : null}
    {"nextCheckpointAt" in effect.changes ? <label><span>Next check</span><input type="datetime-local" value={toLocalDateTime(effect.changes.nextCheckpointAt, board.workspace.timezone)} onChange={(event) => change("nextCheckpointAt", event.target.value ? fromLocalDateTime(event.target.value, board.workspace.timezone) : null)} /></label> : null}
    {"blockedReason" in effect.changes ? <label className="proposal-editor-grid__wide"><span>Blocker</span><textarea rows={2} value={effect.changes.blockedReason ?? ""} onChange={(event) => change("blockedReason", event.target.value || null)} /></label> : null}
    {completeTaskFields || "description" in effect.changes ? <label className="proposal-editor-grid__wide"><span>Task description</span><textarea rows={2} maxLength={10_000} value={String(value("description", current?.description) ?? "")} onChange={(event) => change("description", event.target.value)} /></label> : null}
    {completeTaskFields || "filesReference" in effect.changes ? <label className="proposal-editor-grid__wide"><span>Files location / attachments</span><input maxLength={4_000} value={String(value("filesReference", current?.filesReference) ?? "")} placeholder="HTTP(S) link or plain location" onChange={(event) => change("filesReference", event.target.value || null)} /></label> : null}
    {completeTaskFields || "reviewFeedbackReference" in effect.changes ? <label className="proposal-editor-grid__wide"><span>Review / feedback location</span><input maxLength={4_000} value={String(value("reviewFeedbackReference", current?.reviewFeedbackReference) ?? "")} placeholder="HTTP(S) link or plain location" onChange={(event) => change("reviewFeedbackReference", event.target.value || null)} /></label> : null}
    {effect.note !== null ? <label className="proposal-editor-grid__wide"><span>Note</span><textarea rows={2} value={effect.note} onChange={(event) => setEffect({ ...effect, note: event.target.value || null })} /></label> : null}
  </div>;
}

export function requiresNotifyTarget(type: AgentProposal["type"]): boolean {
  return type === "FOLLOWUP_MESSAGE" || type === "ESCALATION_MESSAGE";
}

export type ProposalAttentionKind = "READY" | "IDENTITY_DECISION" | "WAITING";

// WHY [BOL-ACTIONABLE-PROJECTION-001]: a prepared draft is not automatically a
// decision the owner can complete. Keep send-ready work in Needs action, show
// one strict identity recommendation as a decision, and put all other route or
// membership blockers in Waiting. Falsifier: a disabled Send counts as ready or
// the same unresolved person appears once per overdue task.
export function proposalAttentionKind(proposal: AgentProposal): ProposalAttentionKind {
  if (proposal.effect.kind !== "SEND_MESSAGE") return "READY";
  const destinationReady = proposal.conversationId !== null
    && proposal.effect.conversationId === proposal.conversationId
    && proposal.destinationPolicyReady;
  if (!requiresNotifyTarget(proposal.type)) return destinationReady ? "READY" : "WAITING";
  if (proposal.targetRoute?.state === "IDENTITY_REVIEW" && proposal.targetRoute.recommendation) {
    return "IDENTITY_DECISION";
  }
  return destinationReady && proposal.targetRoute?.state === "READY" ? "READY" : "WAITING";
}

function fieldLabel(field: string): string {
  return ({ title: "Title", description: "Task description", clientId: "Client", projectId: "Project", status: "Status", priority: "Priority", assigneeId: "Assigned to / person", assignedByMemberId: "Assigned by", assignedAt: "Assignment date & time", department: "Department", filesReference: "Files location / attachments", reviewFeedbackReference: "Review / feedback location", dueAt: "Deadline", taskStartAt: "Timeline start", taskEndAt: "Timeline end", nextCheckpointAt: "Next check", blockedReason: "Blocker", workKind: "Type", scheduledStartAt: "Starts", scheduledEndAt: "Ends", scheduledAllDay: "All day" } as Record<string, string>)[field] ?? field;
}

function displayValue(board: BoardSnapshot, field: string, value: unknown, emptyLabel = "Clear"): string {
  if (value === null || value === undefined || value === "") return emptyLabel;
  if (field === "assigneeId" || field === "assignedByMemberId") return board.members.find((member) => member.id === value)?.displayName ?? "Unknown person";
  if (field === "clientId") return board.clients.find((client) => client.id === value)?.name ?? "Unknown client";
  if (field === "projectId") return board.projects.find((project) => project.id === value)?.name ?? "Unknown project";
  if (field === "status" && typeof value === "string") return statusLabel(value);
  if (["taskStartAt", "taskEndAt"].includes(field) && typeof value === "string") return new Intl.DateTimeFormat(undefined, { timeZone: board.workspace.timezone, dateStyle: "medium" }).format(new Date(value));
  if (["assignedAt", "dueAt", "nextCheckpointAt", "scheduledStartAt", "scheduledEndAt"].includes(field) && typeof value === "string") return new Intl.DateTimeFormat(undefined, { timeZone: board.workspace.timezone, dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
  if (field === "scheduledAllDay" && typeof value === "boolean") return value ? "Yes" : "No";
  return String(value).replaceAll("_", " ").toLowerCase();
}

function statusLabel(status: string): string {
  return WORK_STATUS_LABELS[status as keyof typeof WORK_STATUS_LABELS] ?? status.replaceAll("_", " ").toLowerCase();
}
